import { SopImprovementImpactWarningSchema } from "@erp/shared-types";
import { oc, eventIterator } from "@orpc/contract";
import { z } from "zod";

/**
 * Agent session status
 */
export const AgentSessionStatusSchema = z.enum([
  "pending",
  "starting",
  "connecting",
  "authenticating",
  "mfa",
  "connected",
  "running",
  "completed",
  "failed",
  "cancelled",
  "expired",
]);

/**
 * Auth state response (safe to return to client)
 */
export const AgentAuthStateSchema = z.object({
  id: z.string().uuid(),
  organizationId: z.string().uuid(),
  name: z.string(),
  targetUrl: z.string().url(),
  otpTargetId: z.string().nullable(),
  username: z.string().nullable(),
  lastUsedAt: z.coerce.date().nullable(),
  expiresAt: z.coerce.date().nullable(),
  metadata: z.record(z.string(), z.unknown()).nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  isExpired: z.boolean(),
});

/**
 * Get session status input
 */
export const GetSessionStatusInputSchema = z.object({
  organizationId: z.string().uuid(),
  sessionId: z.string().uuid(),
});

/**
 * Screenshot entry with metadata
 */
export const ScreenshotEntrySchema = z.object({
  /** Base64 data, presigned blob URL, or data: URI */
  data: z.string(),
  /** Action that triggered this screenshot */
  action: z.string().optional(),
  /** Timestamp */
  timestamp: z.number(),
  /** A2A messageId of the thread message this frame was captured on (operator path) */
  messageId: z.string().optional(),
});

export const SessionMessageEntrySchema = z.object({
  id: z.string(),
  /** Stable A2A messageId (operator path) — ties screenshots to this entry */
  messageId: z.string().optional(),
  message: z.string(),
  action: z.string().optional(),
  /** Open metadata bag from the A2A message (already redacted) */
  metadata: z.record(z.string(), z.unknown()).optional(),
  level: z.enum(["info", "warn", "error"]).default("info"),
  timestamp: z.number(),
});

/**
 * Session status response
 */
export const SessionStatusResponseSchema = z.object({
  sessionId: z.string().uuid(),
  status: AgentSessionStatusSchema,
  startedAt: z.coerce.date().nullable(),
  completedAt: z.coerce.date().nullable(),
  error: z.string().nullable(),
  /** Latest screenshot (base64) */
  latestScreenshot: z.string().nullable(),
  /** All screenshots captured during the session */
  screenshots: z.array(ScreenshotEntrySchema),
  /** Live agent messages captured during the session */
  messages: z.array(SessionMessageEntrySchema),
  /** Saved auth state ID (if saved) */
  savedAuthStateId: z.string().nullable(),
  /** Saved auth state JSON (for display) */
  savedAuthState: z.string().nullable(),
  /** Live view URL for neko iframe (null when kernel not configured) */
  liveViewUrl: z.string().nullable(),
  /** Document ID for the session recording MP4 (null until recording is uploaded) */
  recordingDocumentId: z.string().nullable(),
  /**
   * Wall-clock instant the recording MP4 was stopped — its true end. The replay UI anchors its
   * timeline to this (recordingStartMs = this - duration) so action markers line up. Null until
   * the recording is finalized, or for non-recorded runs; the UI falls back to completedAt (WF-77).
   */
  recordingStoppedAt: z.coerce.date().nullable(),
});

/**
 * AG-UI live operator stream (AI-244 — see docs/operator/operator-live-stream.md).
 *
 * Canonical AG-UI events: the `type` discriminants + field shapes mirror
 * `@ag-ui/core`'s `EventType` / event schemas exactly (RUN_STARTED, STATE_SNAPSHOT,
 * STATE_DELTA, RUN_FINISHED, RUN_ERROR), so the frontend's `@ag-ui/client`
 * `AbstractAgent` consumes them and auto-reduces STATE_SNAPSHOT/STATE_DELTA into
 * its managed `state`. We keep owned Zod here (not `@ag-ui/core`'s zod-v3 schemas)
 * because oRPC `eventIterator` needs a v4 schema at the boundary — the shapes are
 * kept in lockstep with `@ag-ui/core` by name.
 *
 * The operator panel is carried as the agent **state** (`OperatorActivity`); it
 * mirrors the live subset of SessionStatusResponse so the viewer renders it
 * unchanged.
 */
/**
 * Which lifecycle phase a step belongs to. `setup` runs first (e.g. a login/navigation
 * sequence), `main` is the repeatable task, `teardown` runs last. Set while teaching (the
 * capture toolbar's Setup/Actions/Teardown toggle) and **persisted per step** (see
 * `AgentSopStepSchema.phase`): the composition UI groups the steps into setup / action /
 * teardown zones, and replay runs the setup steps first. A procedure whose steps are all
 * `main` (the common case) renders as a single flat list. */
export const InterviewPhaseSchema = z.enum(["setup", "main", "teardown"]);

/**
 * One step in the live SOP draft an `operator.author_sop` interview is building
 * (folded from the ai-service `sop_step` metadata). `provisional` until the run
 * finalizes — the canonical confirmed SOP is the persisted `.sop.md`. `codeKind`
 * marks how the step replays: `ref` (durable selector), `coordinate` (canvas), or
 * `null` (agentic — e.g. a credential fill, never captured as code).
 */
export const SopStepViewSchema = z.object({
  index: z.number().int(),
  /** Lifecycle phase this step is being taught under (setup / main / teardown). Drives
   * the studio's grouped display; defaults to `main` for older payloads. */
  phase: InterviewPhaseSchema.default("main"),
  name: z.string(),
  intent: z.string().default(""),
  risk: z.enum(["low", "high"]).nullable().default(null),
  status: z.enum(["provisional", "confirmed"]).default("provisional"),
  codeKind: z.enum(["ref", "coordinate"]).nullable().default(null),
  /** Selector durability: `durable` (high-confidence ref) vs `fragile` (coordinate /
   * low-confidence, likely to drift on replay). `null` for non-action/agentic steps. */
  health: z.enum(["durable", "fragile"]).nullable().default(null),
  /** Branch-aware SOPs: a `branch` step carries a condition + labelled alternate paths. Default
   * "action" keeps older payloads valid. */
  kind: z.enum(["action", "branch"]).default("action"),
  /** The path labels of a `branch` step (e.g. ["Matched", "otherwise"]); `[]` on action steps. */
  branchLabels: z.array(z.string()).default([]),
});

/**
 * Which procedure a replay boundary belongs to. Set by the operator's `run_procedure` tool, which
 * can replay SEVERAL procedures in one task — step indices restart at 1 each time, so `index` alone
 * can't tell two procedures' steps apart. Null on the standalone `operator.run_sop` paths, where
 * the ai-service emits no name and Node fills the label from the SOP it is replaying.
 *
 * `ordinal` is 1-based within the run, so two runs of the SAME procedure stay distinct groups.
 */
const SopReplayProcedureSchema = z
  // `.min(1)` matches the published A2A contract (`ReplayProcedureRef.ordinal`, `minimum: 1`) — the
  // upstream producer of this value. Without it the RPC validator accepts 0 and negatives the wire
  // schema rejects, so the two contracts disagree about the same field.
  .object({ name: z.string(), ordinal: z.number().int().min(1) })
  .nullable()
  .default(null);

/**
 * One step's outcome during an `operator.run_sop` replay (folded from the
 * ai-service `sop_replay_step` metadata) — which of the four replay modes ran.
 */
export const SopReplayStepViewSchema = z.object({
  index: z.number().int(),
  name: z.string(),
  mode: z.enum(["deterministic", "healed", "agentic", "failed"]),
  codeKind: z.enum(["ref", "coordinate"]).nullable().default(null),
  // Stable row id (null on the legacy markdown-only path) — lets the frontend correlate this
  // step's persisted trace (AgentRun messages/tool calls) back to the SOP step that produced it.
  stepId: z.string().nullable().default(null),
  // Human-readable failure reason for a `failed` step (why it couldn't be reproduced/healed);
  // null for a step that ran. Surfaced in the replay viewer instead of a bare "failed".
  error: z.string().nullable().default(null),
  // AI-340: the step's Verify post-condition grade — the ONLINE success oracle (a real site exposes
  // no `window.__evalState`). Null when the engine emitted none; within it, `kind` is null for a step
  // with no post-condition, and `ran`/`passed` say whether that check executed and held. Surfaced so
  // the run view can mark which steps are machine-verified vs. resting on the code/agent self-claim.
  verify: z
    .object({
      // Ingestion restores omitted A2A values; API responses always include kind.
      kind: z
        .enum(["url_contains", "text_visible", "python_assert"])
        .nullable(),
      ran: z.boolean(),
      passed: z.boolean(),
    })
    .nullable()
    .default(null),
  // The step succeeded by doing NOTHING: an optional (`best_effort`) step whose target was absent
  // — a popup that didn't appear, an MFA challenge that wasn't issued. Reported as
  // mode="deterministic" like any other success, so without this flag a run that skipped every
  // optional stage is indistinguishable from a full one. Defaults false (also covers payloads
  // from an ai-service that predates it).
  noop: z.boolean().default(false),
  procedure: SopReplayProcedureSchema,
  // Which SOP this step actually came from, when the replayed plan composes several via `sop_ref`.
  // `resolveSopPlan` flattens a composed plan into ONE sequence with plan-global indices, so
  // without this the seams are unrecoverable and a parent embedding two children reads as one flat
  // list. Null on a single-SOP plan (every step trivially belongs to the procedure itself) and on
  // every run recorded before this existed.
  owner: z.string().nullable().default(null),
});

/**
 * One parameter in the live SOP draft (folded from the ai-service `sop_params` metadata, or set
 * by the human via `updateInterviewParameters`). The persisted `SopParamSchema` (below) carries a
 * `default` too; the live view / edit surface only touches these three fields.
 */
export const SopParamViewSchema = z.object({
  // Same charset the replayer templates on (`{[a-z0-9_]+}`) — snake_case, no punctuation.
  name: z.string().regex(/^[a-z0-9_]+$/),
  required: z.boolean().default(true),
  description: z.string().default(""),
});

export const SopDraftViewSchema = z.object({
  steps: z.array(SopStepViewSchema),
  // Parameters declared during the live interview — by the agent's `declare_parameter` as it
  // captures a run-varying value, or by the human editing them directly. Empty until one exists.
  parameters: z.array(SopParamViewSchema).default([]),
  // The lifecycle phase captures currently attribute to (setup / main / teardown). Streamed from
  // the ai-service on every `set_phase` (live toolbar toggle or the agent's tool) so a late joiner
  // — e.g. a page refresh mid-teaching — hydrates the toolbar from the server phase instead of
  // resetting to `main` and silently mis-attributing the next captured steps.
  currentPhase: InterviewPhaseSchema.default("main"),
});

/**
 * The step currently mid-replay (folded from the ai-service `sop_replay_step_started`
 * metadata). Set when a step begins and cleared by that step's `sop_replay_step`
 * completion, so the live view shows an in-progress row instead of appearing stalled
 * while a long (e.g. agentic) step runs. No mode/verify yet — those only exist once
 * the step resolves into a `SopReplayStepView`.
 */
export const SopReplayRunningStepSchema = z.object({
  index: z.number().int(),
  name: z.string(),
  stepId: z.string().nullable().default(null),
  procedure: SopReplayProcedureSchema,
  // The step's authored instruction, when the replay plan carries one. Absent on an ai-service
  // that predates it.
  description: z.string().nullish(),
});

export const SopReplayViewSchema = z.object({
  steps: z.array(SopReplayStepViewSchema),
  // Null when no step is mid-flight — including on an ai-service that predates the
  // start signal, so consumers must not rely on it ever being set.
  running: SopReplayRunningStepSchema.nullable().default(null),
});

export const AwaitingInputSchema = z.object({
  question: z.string(),
  // "credentials" when the pause is a request_credentials tool call (author_sop's driver.py) —
  // the frontend renders a credentials card instead of the plain yes/no reply for that case.
  // "confirm_action" is the mock_ui.studio per-screen confirm loop (AI-340): the frontend lets the
  // user DRIVE the live preview to perform the action in `question`, then records what they did.
  kind: z.enum(["confirm", "credentials", "confirm_action"]).default("confirm"),
  domain: z.string().nullable().default(null),
});

// --- Mock UI Studio eval-case forest (AI-340) ---
// A `mock_ui.studio` build emits a `paths` artifact: the logical trees that make the generated
// mock a deterministic operator eval target. One entry per `?case=` scenario, each pairing the
// correct decision path with the expected `window.__evalState` leaf the eval harness scores.

export const MockUiEvalCaseSchema = z.object({
  /** The `?case=` id that seeds this scenario. */
  id: z.string(),
  /** One-line scenario / task the operator would be asked to complete. */
  task: z.string().default(""),
  /** Plain-English pass condition for this case — what a correct completion looks like. Drives the
   * rubric judge when this case is run as an operator eval (empty when the build didn't author one). */
  successCriteria: z.string().default(""),
  /** The correct decision path — ordered action labels the agent should take. */
  path: z.array(z.string()).default([]),
  /** Screenshot per step, index-aligned to `path` — the screen after that step (blob URL, or a
   * `data:` URI in local dev). Filled by the finalize capture pass; empty string = capture missed
   * that step. May be shorter than `path` if capture was truncated. */
  shots: z.array(z.string()).default([]),
  /** Expected `window.__evalState` leaf the eval harness scores against (pass state). */
  expect: z.record(z.string(), z.unknown()).default({}),
});

export const MockUiPathsSchema = z.object({
  site: z.string().default(""),
  /** Screen/nav labels the mock exposes (the screen tree, flattened). */
  screens: z.array(z.string()).default([]),
  /** The eval-case forest: one entry per `?case=` scenario. */
  cases: z.array(MockUiEvalCaseSchema).default([]),
  /** Durable public URL of the mock on S3 static hosting, when hosting is configured — the eval
   * and demo run against this stable URL instead of the ephemeral, signed Daytona preview. Null
   * when hosting isn't set up (the caller falls back to the preview URL). */
  hostedUrl: z.string().nullable().default(null),
});

/** One storyboard frame: a source screenshot (self-contained `data:` URI so it persists with the
 * artifact and never expires) + the user's inline note. Ordered; frame i pairs with the primary
 * case's step i for the side-by-side comparison. */
export const MockUiStoryboardFrameSchema = z.object({
  source: z.string(),
  note: z.string().default(""),
});
export const MockUiStoryboardSchema = z.array(MockUiStoryboardFrameSchema);

// --- Mock UI Studio state graph (AI-340) — the persisted, evaluable state machine ---
// The agent-authored (or, for legacy mocks, cases-derived) `state_graph` artifact. A NODE is a
// state (its meaning + captured `window.__evalState` + screenshot), an EDGE is an action the
// operator takes between states, a PATH is a traversal (== one `?case=` scenario). Mirrors the
// Pydantic model in `services/ai-service/src/ai_service/a2a/mock_ui/graph.py` (camelCase wire).

/** Deep-link to a state via the existing `?case=<case>&step=<step>` replay; step -1 = initial. */
export const MockUiStateAnchorSchema = z.object({
  case: z.string().default(""),
  step: z.number().int().default(-1),
});

export const MockUiStateNodeSchema = z.object({
  /** Stable node id (agent-assigned, or derived from the action prefix). */
  id: z.string(),
  label: z.string().default(""),
  screen: z.string().default(""),
  anchor: MockUiStateAnchorSchema.default(() => ({ case: "", step: -1 })),
  /** Expected `window.__evalState` once this state is reached (non-authoritative in v1). */
  expect: z.record(z.string(), z.unknown()).default({}),
  /** Design-by-contract prose stamped at derive time (keyed by the `states[]` label): what must
   * hold to be in this state. Machine form is `expect` + graph topology; this is the prose the
   * eval's rubric judge assesses. */
  precondition: z.string().default(""),
  /** RFC-2119 MUST NOT clauses for this state. Each becomes an individually-id'd ContractCheck. */
  prohibitions: z.array(z.string()).default([]),
  isRoot: z.boolean().default(false),
  isTerminal: z.boolean().default(false),
  /** Machine-filled by the capture pass: the captured `window.__evalState` at this state. */
  evalStateSnapshot: z.record(z.string(), z.unknown()).default({}),
  /** Machine-filled by the capture pass: screenshot URI ("" = capture missed). */
  screenshot: z.string().default(""),
  /** Projected on read (AI-340): the golden `window.__evalState` of the SOP taught for the path
   * this node terminates — the deterministic eval's assertion. Null/absent unless this is a
   * taught path's terminal node with a captured golden. NOT emitted by the coding agent. */
  golden: z.record(z.string(), z.unknown()).nullable().optional(),
});

/** An action edge — `source`/`target` are node ids (React Flow-shaped so the UI consumes them
 * directly); `action` is the human label; `selector` is an optional element hint. */
export const MockUiStateEdgeSchema = z.object({
  id: z.string(),
  source: z.string(),
  target: z.string(),
  action: z.string().default(""),
  actionKind: z.string().default(""),
  selector: z.string().default(""),
  caseIds: z.array(z.string()).default([]),
});

/** A path (== a `?case=` scenario) as an ordered traversal of nodes/edges. */
export const MockUiStatePathSchema = z.object({
  id: z.string(),
  task: z.string().default(""),
  nodeIds: z.array(z.string()).default([]),
  edgeIds: z.array(z.string()).default([]),
  /** Per-case binding of the graph's `parameters` to concrete values (e.g. {"po_number":"48213"}).
   * `{name}` tokens in this path's task/state text resolve against this map at run time. */
  params: z.record(z.string(), z.string()).default({}),
  /** Projected on read (AI-340): the id of the SOP taught against this path's `?case=` — the
   * path's executable walk. Null/absent until a procedure is taught. NOT emitted by the agent. */
  sopId: z.string().uuid().nullable().optional(),
});

/** One declared SOP parameter. Mirrors `SopParam` (snake_case name enforced server-side). Defined
 * here (ahead of the other SOP schemas) so `MockUiParamSchema` can share its shape. */
export const SopParamSchema = z.object({
  name: z.string(),
  required: z.boolean(),
  description: z.string(),
  default: z.string().nullable().default(null),
});

/** A run-time parameter the whole mock/procedure is templated on (mirrors `GraphParam` in
 * `mock_ui/graph.py`). Reuses `SopParamSchema`'s shape but defaults `required`/`description` so the
 * mock-authoring path can omit them. Referenced as `{name}` tokens in prose; bound per case via
 * `MockUiStatePathSchema.params`. */
export const MockUiParamSchema = SopParamSchema.extend({
  // Same charset as the tokenizer (`{[a-z0-9_]+}` in mock_ui/finalize.py) so a declared name always
  // matches how it's referenced; rejects empty/mixed-case/punctuation at the RPC boundary.
  name: z.string().regex(/^[a-z0-9_]+$/),
  required: z.boolean().default(true),
  description: z.string().default(""),
});

export const MockUiStateGraphSchema = z.object({
  version: z.number().int().default(1),
  site: z.string().default(""),
  screens: z.array(z.string()).default([]),
  /** True once the agent declared the graph; false when derived from `eval-paths.json` cases. */
  authored: z.boolean().default(false),
  nodes: z.array(MockUiStateNodeSchema).default([]),
  edges: z.array(MockUiStateEdgeSchema).default([]),
  paths: z.array(MockUiStatePathSchema).default([]),
  /** Run-time parameters declared once for the whole mock (bound per case in each path's `params`). */
  parameters: z.array(MockUiParamSchema).default([]),
});

export const OperatorActivitySchema = z.object({
  status: AgentSessionStatusSchema,
  liveViewUrl: z.string().nullable(),
  messages: z.array(SessionMessageEntrySchema),
  screenshots: z.array(ScreenshotEntrySchema),
  latestScreenshot: z.string().nullable(),
  /** Live SOP draft during an `operator.author_sop` interview (empty otherwise). */
  sopDraft: SopDraftViewSchema.default(() => ({
    steps: [],
    parameters: [],
    currentPhase: "main" as const,
  })),
  /** Per-step replay progress during an `operator.run_sop` run (empty otherwise). */
  sopReplay: SopReplayViewSchema.default(() => ({ steps: [], running: null })),
  /** Set while the interview is paused awaiting a human yes/no/correction. */
  awaitingInput: AwaitingInputSchema.nullable().default(null),
  /** Eval-case forest from a `mock_ui.studio` build's `paths` artifact (null for other skills). */
  mockUiPaths: MockUiPathsSchema.nullable().default(null),
  /** State machine from a `mock_ui.studio` build's `state_graph` artifact (null for other skills). */
  mockUiStateGraph: MockUiStateGraphSchema.nullable().default(null),
  /** Ordered source screenshots + notes uploaded on start (null for non-storyboard builds). */
  storyboard: MockUiStoryboardSchema.nullable().default(null),
});

/** RFC-6902 JSON Patch op subset emitted on STATE_DELTA (add/replace/remove).
 * `value` is omitted for `remove`. */
export const OperatorStatePatchOpSchema = z.object({
  op: z.enum(["add", "replace", "remove"]),
  path: z.string(),
  value: z.unknown().optional(),
});

/**
 * AG-UI event subset for the live operator session (discriminated on `type`).
 * Names + fields match `@ag-ui/core`: STATE_* carry no run id; lifecycle events
 * carry `threadId` + `runId`.
 */
export const OperatorAgUiEventSchema = z.discriminatedUnion("type", [
  z.object({
    type: z.literal("RUN_STARTED"),
    threadId: z.string(),
    runId: z.string(),
    timestamp: z.number().optional(),
  }),
  z.object({
    type: z.literal("STATE_SNAPSHOT"),
    snapshot: OperatorActivitySchema,
    timestamp: z.number().optional(),
  }),
  z.object({
    type: z.literal("STATE_DELTA"),
    delta: z.array(OperatorStatePatchOpSchema),
    timestamp: z.number().optional(),
  }),
  z.object({
    type: z.literal("RUN_FINISHED"),
    threadId: z.string(),
    runId: z.string(),
    result: z
      .object({
        success: z.boolean(),
        summary: z.string().nullable().optional(),
      })
      .optional(),
    timestamp: z.number().optional(),
  }),
  z.object({
    type: z.literal("RUN_ERROR"),
    message: z.string(),
    code: z.string().optional(),
    timestamp: z.number().optional(),
  }),
]);

export const SubscribeOperatorSessionInputSchema = z.object({
  organizationId: z.string().uuid(),
  sessionId: z.string().uuid(),
  /** Client-generated id echoed on every event for this subscription. */
  runId: z.string(),
  /** Chat thread id when the operator is embedded in chat (optional). */
  threadId: z.string().optional(),
});

/**
 * Cancel session input
 */
export const CancelSessionInputSchema = z.object({
  organizationId: z.string().uuid(),
  sessionId: z.string().uuid(),
});

/**
 * Cancel session response
 */
export const CancelSessionResponseSchema = z.object({
  success: z.boolean(),
  message: z.string(),
});

/**
 * List auth states input
 */
export const ListAuthStatesInputSchema = z.object({
  organizationId: z.string().uuid(),
  limit: z.number().int().min(1).max(100).default(20),
  offset: z.number().int().min(0).default(0),
});

/**
 * List auth states response
 */
export const ListAuthStatesResponseSchema = z.object({
  authStates: z.array(AgentAuthStateSchema),
  pagination: z.object({
    total: z.number(),
    limit: z.number(),
    offset: z.number(),
    hasMore: z.boolean(),
  }),
});

/**
 * Delete auth state input
 */
export const DeleteAuthStateInputSchema = z.object({
  organizationId: z.string().uuid(),
  authStateId: z.string().uuid(),
});

/**
 * Delete auth state response
 */
export const DeleteAuthStateResponseSchema = z.object({
  success: z.boolean(),
  message: z.string(),
});

// --- Bidirectional interview → SOP authoring (AI-166) ---

/** Optional model override for an interview / replay run (defaults resolved server-side). */
export const AgentRunModelInputSchema = z.object({
  provider: z.string().optional(),
  modelId: z.string().optional(),
});

export const StartInterviewInputSchema = z.object({
  organizationId: z.string().uuid(),
  userId: z.string().uuid().nullable().optional(),
  /** What procedure the human wants to teach — seeds the interview. */
  instruction: z.string().min(1),
  startUrl: z.string().url().optional(),
  credentialSetId: z.string().uuid().nullable().optional(),
  /** An existing SOP to run first, against the same browser, before the live teaching turn
   * starts — e.g. a login/navigation sequence that's already captured elsewhere. On
   * finalize it's also embedded as the taught SOP's first (`sop_ref`) step, so replays
   * run it automatically too. */
  setupSopId: z.string().uuid().nullable().optional(),
  /** An existing SOP to attach to the taught procedure as its fallback login (AI-246
   * follow-up) — persisted on finalize as the SOP's own login config; never run during the
   * interview itself. A future replay runs it only if that replay's Kernel pre-auth (via
   * the taught SOP's remembered `credentialSetId`) doesn't succeed. */
  fallbackLoginSopId: z.string().uuid().nullable().optional(),
  maxIterations: z.number().int().min(1).max(200).optional(),
  model: AgentRunModelInputSchema.optional(),
  /** Autonomy ladder: how often the agent pauses to check in (AI-246 trust loop).
   * Defaults to `high_risk_only` server-side when omitted. */
  pausePolicy: z
    .enum(["every_step", "high_risk_only", "autonomous"])
    .optional(),
  /** Free-roaming live capture (AI-246 free-roam): arm the in-page gesture recorder, park with a
   * deterministic handoff, then let the human drive the SOP via "capture now" turns rather than
   * an agent-led step-by-step interview. Maps to `AuthorSopConfig.free_roam`; off by default. */
  freeRoam: z.boolean().optional(),
});

/** Both start* procedures return a session id; the client then subscribes via
 * `subscribeOperatorSession` (the AG-UI stream is shared across all operator skills). */
export const StartSessionIdResponseSchema = z.object({
  sessionId: z.string().uuid(),
});

export const RespondToInterviewInputSchema = z.object({
  organizationId: z.string().uuid(),
  sessionId: z.string().uuid(),
  /** The human's reply to the pending confirm: yes / a correction / a retraction. */
  answer: z.string().min(1),
});

export const RespondToInterviewResponseSchema = z.object({
  ok: z.boolean(),
});

/** "Jump in" — interrupt a running interview turn at its next tool boundary so the human can
 * guide it (AI-246 trust loop). Pauses via the Strands interrupt mechanism; does NOT cancel. */
export const InterruptInterviewInputSchema = z.object({
  organizationId: z.string().uuid(),
  sessionId: z.string().uuid(),
});

// --- Interactive interview: annotate mode (AI-246) ---
// While the interview is paused at a question, the human can draw on the live view instead of
// (or alongside) typing. Strokes are sent as VECTORS in normalized [0,1] coords relative to the
// live-view rect — viewport-independent, and resolvable server-side to @eN refs (stronger than
// pixels). The composited screenshot is NOT sent (the live view is a cross-origin iframe we can't
// read); the ai-service attaches a fresh server-side screenshot instead.

export const AnnotationIntentSchema = z.enum(["act_here", "avoid", "note"]);

export const AnnotationStrokeSchema = z.object({
  /** What the mark means — declared by the user, not guessed from pixels. */
  intent: AnnotationIntentSchema,
  kind: z.enum(["freehand", "circle", "arrow"]).default("freehand"),
  /** Path points in normalized [0,1] coords relative to the live-view rect. */
  points: z
    .array(
      z.object({ x: z.number().min(0).max(1), y: z.number().min(0).max(1) }),
    )
    .min(1),
});

export const SubmitInterviewAnnotationInputSchema = z
  .object({
    organizationId: z.string().uuid(),
    sessionId: z.string().uuid(),
    /** Optional free-text note to accompany the marks. */
    note: z.string().max(2000).optional(),
    strokes: z.array(AnnotationStrokeSchema).max(50).default([]),
  })
  // An empty annotation (no strokes, no note) carries no signal — reject it client- and server-side.
  .refine((v) => v.strokes.length > 0 || (v.note?.trim().length ?? 0) > 0, {
    message: "Provide at least one mark or a note.",
  });

export const SubmitInterviewAnnotationResponseSchema = z.object({
  ok: z.boolean(),
});

// --- Interactive interview: takeover mode (AI-246) ---
// While paused, the human can drive the REAL browser through the interactive Kernel live view.
// Their gestures are captured in-page as best-effort hints; this signal resumes the interview so
// the agent reconciles those hints against the live page into durable, human-authored steps.
export const SubmitInterviewTakeoverInputSchema = z.object({
  organizationId: z.string().uuid(),
  sessionId: z.string().uuid(),
});

export const SubmitInterviewTakeoverResponseSchema = z.object({
  ok: z.boolean(),
});

// --- Interactive interview: "capture now" (AI-246 free-roam) ---
// In a free-roam session the human walks the REAL production site in the interactive Kernel live
// view, then presses "capture now". Like takeover this carries NO payload — the gestures live
// server-side in the browser page's `window.__sopObserved` buffer and the screenshot is taken
// server-side. This resumes the parked task so the ai-service drains those gestures + screenshot
// and the agent appends/refines a linear SOP step, recording one trajectory frame.
export const SubmitInterviewCaptureInputSchema = z.object({
  organizationId: z.string().uuid(),
  sessionId: z.string().uuid(),
});

export const SubmitInterviewCaptureResponseSchema = z.object({
  ok: z.boolean(),
});

// --- Interactive interview: "set the active phase" (setup / main / teardown) ---
// A teaching session declares which lifecycle phase it is currently teaching so that steps
// captured afterward attribute to that phase. `setup` (e.g. a login/navigation sequence) runs
// first, `teardown` runs last, `main` is the repeatable task; each step's phase is persisted so
// the composition UI can group them into zones. Like capture/branch this resumes the parked task
// with a `{ kind: "set_phase" }` directive so a free-roam session applies the switch mid-run.
// Default phase is `main` — a session that never sets a phase produces a single flat procedure.
export const SetInterviewPhaseInputSchema = z.object({
  organizationId: z.string().uuid(),
  sessionId: z.string().uuid(),
  phase: InterviewPhaseSchema,
});

export const SetInterviewPhaseResponseSchema = z.object({
  ok: z.boolean(),
});

// --- Interactive interview: "start an alternate path" (P3 branch capture) ---
// In a free-roam session the human presses "start an alternate path" to demonstrate a branch: an
// alternate walkthrough that shares a prefix with the current path, then diverges under a stated
// condition. Unlike capture this carries a payload (the condition + an optional divergence point)
// but NO screenshot/gestures — a branch only marks the fork; the captures that follow attribute to
// the new path. This resumes the parked task with a `{ kind: "branch" }` directive whose
// camelCase fields map to the snake_case `condition_label` / `from_frame_index` the ai-service
// reads. `fromFrameIndex` is a 1-based frame index to diverge FROM; omit to diverge from the
// current (latest) frame of the active path.
export const SubmitInterviewBranchInputSchema = z.object({
  organizationId: z.string().uuid(),
  sessionId: z.string().uuid(),
  conditionLabel: z.string().min(1),
  fromFrameIndex: z.number().int().positive().optional(),
});

export const SubmitInterviewBranchResponseSchema = z.object({
  ok: z.boolean(),
});

// --- Interactive interview: credentials request (AI-246) ---
// The interview agent paused via request_credentials (a login wall it had no usable
// credentials for). The frontend has already persisted a credential via the generic
// credentials.create RPC; this just points the paused run at it and resumes.
export const SubmitInterviewCredentialsInputSchema = z.object({
  organizationId: z.string().uuid(),
  sessionId: z.string().uuid(),
  credentialId: z.string().uuid(),
});

export const SubmitInterviewCredentialsResponseSchema = z.object({
  ok: z.boolean(),
});

// --- Interactive interview: human parameter edits (add / edit / remove) ---
// The human curates the draft's run-time parameters during capture. This replaces the whole list
// (the frontend sends the full desired set): the interview runner stores it as authoritative and
// rides it on the next capture/branch/done directive so the ai-service draft is seeded from it,
// and surfaces it live as `sopDraft.parameters`. Names are snake_case (replayer templating).
export const UpdateInterviewParametersInputSchema = z.object({
  organizationId: z.string().uuid(),
  sessionId: z.string().uuid(),
  parameters: z.array(SopParamViewSchema),
});

export const UpdateInterviewParametersResponseSchema = z.object({
  ok: z.boolean(),
});

export const FinalizeInterviewInputSchema = z.object({
  organizationId: z.string().uuid(),
  sessionId: z.string().uuid(),
  /** Kebab slug, unique per org. */
  name: z.string().min(1).max(255),
  title: z.string().min(1).max(255).optional(),
  siteKey: z.string().max(255).optional(),
  taskKey: z.string().max(255).optional(),
});

/** Import an existing markdown SOP as a new (skeleton) procedure — the steps are parsed
 * deterministically server-side. `title`/`name` override the values derived from the markdown. */
export const ImportSopInputSchema = z.object({
  organizationId: z.string().uuid(),
  bodyMarkdown: z.string().min(1).max(200_000),
  title: z.string().min(1).max(255).nullish(),
  /** Optional kebab slug; derived from the title when omitted, and de-duplicated per org. */
  name: z.string().min(1).max(255).nullish(),
  /** Import a document whose shape the parser doesn't recognize: the ai-service restructures it
   * into canonical steps (blank-line-separated steps when that's unavailable). Set only after the
   * author confirms the NO_SOP_STEPS_DETECTED prompt, so an accidental paste still fails loudly
   * on the first attempt. */
  allowUnstructured: z.boolean().optional(),
});

/** A source supported by the asynchronous, review-first SOP importer. */
export const SopImportSourceSchema = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("scribe"),
    url: z.string().url().max(2_000),
  }),
  z.object({
    kind: z.literal("markdown"),
    bodyMarkdown: z.string().min(1).max(200_000),
    title: z.string().min(1).max(255).optional(),
  }),
]);

export const SopImportStatusSchema = z.enum([
  "queued",
  "processing",
  "ready",
  "failed",
  "accepted",
  "discarded",
]);

export const SopImportCandidateStepSchema = z.object({
  stepId: z.string().min(1).max(255),
  name: z.string().min(1).max(255),
  description: z.string().default(""),
  constraints: z.array(z.string()).default([]),
  evidenceAssetKeys: z.array(z.string()).default([]),
});

export const SopImportWarningSchema = z.object({
  code: z.string().min(1),
  message: z.string().min(1),
  stepId: z.string().optional(),
});

export const SopImportCandidateSchema = z.object({
  schemaVersion: z.literal(1),
  title: z.string().min(1).max(255),
  bodyMarkdown: z.string().min(1).max(200_000),
  steps: z.array(SopImportCandidateStepSchema).min(1),
  source: z.object({
    kind: z.enum(["scribe", "markdown"]),
    canonicalUrl: z.string().url().nullable(),
    capturedAt: z.coerce.date(),
    contentSha256: z.string().regex(/^[a-f0-9]{64}$/),
  }),
  warnings: z.array(SopImportWarningSchema).default([]),
});

export const SopImportAssetSchema = z.object({
  assetKey: z.string().min(1),
  sourceUrl: z.string().url(),
  /** Short-lived URL to the retained copy. Regenerated whenever the review is read. */
  evidenceUrl: z.string().url().nullable(),
  contentType: z.enum(["image/jpeg", "image/png", "image/webp"]),
  documentId: z.string().uuid().nullable(),
  contentSha256: z
    .string()
    .regex(/^[a-f0-9]{64}$/)
    .nullable(),
  width: z.number().int().positive().nullable(),
  height: z.number().int().positive().nullable(),
  caption: z.string().nullable(),
  ordinal: z.number().int().positive(),
  /** Scribe click highlights detected by the import (provenance `scribe_ring`); the review page
   * seeds the frame's marks from them. Lazy: the annotation schema is declared further down. */
  suggestedAnnotations: z
    .array(z.lazy(() => GuideAnnotationSchema))
    .default([]),
});

export const SopImportSchema = z.object({
  id: z.string().uuid(),
  organizationId: z.string().uuid(),
  requestedByUserId: z.string().uuid().nullable(),
  source: SopImportSourceSchema,
  status: SopImportStatusSchema,
  candidate: SopImportCandidateSchema.nullable(),
  assets: z.array(SopImportAssetSchema).default([]),
  backgroundJobId: z.string().uuid().nullable(),
  acceptedSopId: z.string().uuid().nullable(),
  errorCode: z.string().nullable(),
  errorMessage: z.string().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

export const StartSopImportInputSchema = z.object({
  organizationId: z.string().uuid(),
  source: SopImportSourceSchema,
});

export const StartSopImportResponseSchema = z.object({
  importId: z.string().uuid(),
  backgroundJobId: z.string().uuid(),
});

export const GetSopImportInputSchema = z.object({
  organizationId: z.string().uuid(),
  importId: z.string().uuid(),
});

export const RetrySopImportInputSchema = GetSopImportInputSchema;
export const DiscardSopImportInputSchema = GetSopImportInputSchema;

// --- SOP visual guide (AI-488 follow-on) ----------------------------------------------------
// A version's guide is an ordered set of Document-backed screenshots ("frames") bound to step ids,
// each carrying vector annotations in image-normalized [0,1] coordinates (the editable source of
// truth) from which a flattened rendering is derived for the replay agent. Frames belong to the
// VERSION and copy forward with it; edges are NOT stored — the SOP's step order and branch nodes
// already are the topology.
export const SopGuideModeSchema = z.enum(["off", "on_heal", "always"]);
export const SopGuideScreenTypeSchema = z.enum([
  "login",
  "file-upload",
  "file-download",
]);
/** What a mark means — declared by the author, never guessed from pixels. `act_here` is where the
 * author acted (the future `visual` target), `read` is what they checked (a verify candidate),
 * `avoid` is a control not to touch, `note` is commentary. */
export const GuideAnnotationIntentSchema = z.enum([
  "act_here",
  "read",
  "avoid",
  "note",
]);
/** Mirrors the frontend Annotator's tools (`pen→freehand`, `arrow`, `rect`, `point→marker`,
 * `text`) plus `circle` for Scribe's own click ring. */
export const GuideAnnotationKindSchema = z.enum([
  "freehand",
  "circle",
  "arrow",
  "rect",
  "marker",
  "text",
]);
export const GuideAnnotationSchema = z.object({
  id: z.string().min(1).max(64),
  intent: GuideAnnotationIntentSchema,
  kind: GuideAnnotationKindSchema,
  /** Normalized to the BASE image (not a viewport): freehand = the path; arrow/rect = [from, to];
   * marker/text = one point; circle = [centre, edge]. */
  points: z
    .array(
      z.object({ x: z.number().min(0).max(1), y: z.number().min(0).max(1) }),
    )
    .min(1)
    .max(2_000),
  /** Short label spoken to the model ("Save button") or the text tool's text. */
  label: z.string().max(120).optional(),
  /** Numbered-marker index (kind=marker). */
  n: z.number().int().positive().optional(),
  color: z.string().max(32).optional(),
  /** Who drew it: a person, a detected Scribe click ring, or a vision pass. */
  provenance: z.enum(["human", "scribe_ring", "model"]).default("human"),
});
export const GuideFrameSourceSchema = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("import"),
    importId: z.string().uuid(),
    assetId: z.string().uuid(),
  }),
  z.object({
    kind: z.literal("trajectory"),
    sessionId: z.string().uuid(),
    frameIndex: z.number().int().nonnegative(),
  }),
  z.object({
    kind: z.literal("replay"),
    agentRunId: z.string().uuid(),
    stepId: z.string().min(1),
  }),
  z.object({ kind: z.literal("upload") }),
]);
export const SopGuideFrameSchema = z.object({
  id: z.string().uuid(),
  ordinal: z.number().int(),
  label: z.string(),
  screenType: SopGuideScreenTypeSchema.nullable(),
  annotations: z.array(GuideAnnotationSchema).default([]),
  source: GuideFrameSourceSchema,
  provenance: z.enum(["provisional", "confirmed"]),
  width: z.number().int().positive().nullable(),
  height: z.number().int().positive().nullable(),
  capturedAt: z.coerce.date().nullable(),
  /** Step ids this frame shows (the screen as it looked BEFORE acting on that step). */
  stepIds: z.array(z.string()).default([]),
  /** Short-lived signed URLs, resolved on read; null when the retained blob is unreadable. */
  baseUrl: z.string().url().nullable(),
  flattenedUrl: z.string().url().nullable(),
});
export const MAX_SOP_GUIDE_FRAMES = 30;
export const ListSopGuideInputSchema = z.object({
  organizationId: z.string().uuid(),
  sopId: z.string().uuid(),
  /** Omitted → the published version. */
  version: z.number().int().positive().optional(),
});
export const ListSopGuideResponseSchema = z.object({
  versionId: z.string().uuid().nullable(),
  mode: SopGuideModeSchema,
  frames: z.array(SopGuideFrameSchema),
});
export const SetSopGuideModeInputSchema = z.object({
  organizationId: z.string().uuid(),
  sopId: z.string().uuid(),
  /** The caller's open draft (published versions are immutable). */
  draftVersionId: z.string().uuid(),
  mode: SopGuideModeSchema,
});
export const SetSopGuideModeResponseSchema = z.object({
  mode: SopGuideModeSchema,
});
const SopGuideDraftEditSchema = z.object({
  organizationId: z.string().uuid(),
  sopId: z.string().uuid(),
  draftVersionId: z.string().uuid(),
});
export const SaveSopGuideAnnotationsInputSchema =
  SopGuideDraftEditSchema.extend({
    frameId: z.string().uuid(),
    annotations: z.array(GuideAnnotationSchema).max(200),
  });
export const BindSopGuideFrameInputSchema = SopGuideDraftEditSchema.extend({
  frameId: z.string().uuid(),
  stepIds: z.array(z.string().min(1).max(255)).max(50),
});
export const ReorderSopGuideFramesInputSchema = SopGuideDraftEditSchema.extend({
  frameIds: z.array(z.string().uuid()).max(MAX_SOP_GUIDE_FRAMES),
});
export const RemoveSopGuideFrameInputSchema = SopGuideDraftEditSchema.extend({
  frameId: z.string().uuid(),
});
export const AddSopGuideUploadInputSchema = SopGuideDraftEditSchema.extend({
  /** `data:image/(png|jpeg|webp);base64,…`, ≤8 MB decoded. */
  imageDataUri: z.string().min(1).max(12_000_000),
  label: z.string().max(255).optional(),
  screenType: SopGuideScreenTypeSchema.optional(),
  stepIds: z.array(z.string().min(1).max(255)).max(50).default([]),
});
export const SopGuideFramesResponseSchema = z.object({
  frames: z.array(SopGuideFrameSchema),
});
export const RefreshSopGuideFromRunInputSchema = SopGuideDraftEditSchema.extend(
  {
    /** Omitted → the latest completed replay of the draft's parent version. */
    agentRunId: z.string().uuid().optional(),
  },
);
export const SopGuideDriftInputSchema = z.object({
  organizationId: z.string().uuid(),
  sopId: z.string().uuid(),
  version: z.number().int().positive().optional(),
  agentRunId: z.string().uuid().optional(),
});
export const SopGuideDriftResponseSchema = z.object({
  agentRunId: z.string().uuid().nullable(),
  frames: z.array(
    z.object({
      frameId: z.string().uuid(),
      /** Differing perceptual-hash bits (0–64); null when the run had no screenshot to compare. */
      distance: z.number().int().min(0).max(64).nullable(),
      stale: z.boolean(),
    }),
  ),
});
/** One reviewed frame on import accept: which asset, what it shows, what was drawn on it. */
export const AcceptSopImportFrameInputSchema = z.object({
  assetKey: z.string().min(1).max(255),
  label: z.string().max(255).optional(),
  screenType: SopGuideScreenTypeSchema.optional(),
  stepIds: z.array(z.string().min(1).max(255)).max(50).default([]),
  annotations: z.array(GuideAnnotationSchema).max(200).default([]),
});

export const AcceptSopImportInputSchema = GetSopImportInputSchema.extend({
  /** Optional collision-safe slug seed. The candidate title is used when omitted. */
  name: z.string().min(1).max(255).optional(),
  /** Reviewed guide frames (order = guide order). Omitted → one frame per retained asset, bound
   * via the candidate steps' `evidenceAssetKeys`. */
  frames: z
    .array(AcceptSopImportFrameInputSchema)
    .max(MAX_SOP_GUIDE_FRAMES)
    .optional(),
});

/** Rejection message for an import with no recognizable step structure. Shared so the client can
 * tell this recoverable case apart (offer "build it anyway") from a real import failure. */
export const NO_SOP_STEPS_DETECTED_MESSAGE =
  "No steps detected in the markdown.";

/** Statuses this feature writes + filters by. Reads tolerate any stored value
 * (a SOP table shared with older tooling may carry e.g. "published"). */
export const SopStatusSchema = z.enum(["draft", "active", "archived"]);

// --- Persisted SOP steps, parameters & composition (AI-246) ---
// Steps are now durable rows (`ai_agent_sop_steps`), not prose parsed from the markdown.
// These shapes MIRROR the ai-service Pydantic models (author_sop/sop_draft.py) so the
// JSON that crosses the A2A boundary round-trips into typed rows. `bodyMarkdown` is
// demoted to a render cache of the authored (action-only) steps.

/** Durable handle to the element a step acted on. `code_locator` (semantic, healable) and
 * `visual` (pixel, not healable) are the live kinds; `selector`/`a11y_ref` are tolerated for
 * forward-compat. Mirrors `StepTarget`. */
export const StepTargetSchema = z.object({
  kind: z.enum(["code_locator", "visual", "selector", "a11y_ref"]),
  value: z.string(),
  confidence: z.enum(["high", "low"]).default("high"),
});

/** A value-free deterministic credential fill (mirrors the ai-service `CredentialFill`): the
 * surface fills `credentialName`'s value into `selector` at replay time — the secret itself
 * never appears in stored steps, sandboxes, or screenshots.
 *
 * An EMPTY `selector` is the in-desktop (canvas) variant: the secret is typed by keyboard into
 * whatever element currently has focus, because an app streamed inside a remote desktop (Famous in
 * AVD) has no DOM field to address. The preceding step — an ordinary coordinate click, which
 * hardens and self-heals like any other — is what puts the cursor in the field. The ai-service
 * honors it only on a visual replay (`AgentSop.visualOnly`); on a DOM page it fails the step rather
 * than blind-typing a password, so a web SOP whose selector went missing breaks loudly. */
export const CredentialFillSchema = z.object({
  selector: z.string().default(""),
  credentialName: z.string().min(1),
  submit: z.boolean().default(false),
  submitSelector: z.string().default(""),
  visibleTimeoutMs: z.number().int().positive().default(5000),
});

/** A step's deterministic action. `code` may read run-time params via `params["name"]`.
 * Mirrors `StepAction` (ai-service `sop_draft.py`) field-for-field, incl. the reserved `params`
 * map — so the authored-step JSON round-trips without Zod silently stripping it. A
 * `credential_fill` action carries `credential` (and no `code` — it is filled by the surface,
 * never healed, never hardened into a script). */
export const StepActionSchema = z.object({
  kind: z.enum(["script", "skill_ref", "credential_fill"]).default("script"),
  language: z.enum(["python", "js"]).default("python"),
  code: z.string().default(""),
  skill: z.string().nullable().default(null),
  params: z.record(z.string(), z.string()).default({}),
  credential: CredentialFillSchema.nullable().default(null),
});

/** Optional post-condition proving a step worked. Mirrors `StepVerify`. */
export const StepVerifySchema = z.object({
  kind: z.enum(["url_contains", "text_visible", "python_assert"]),
  value: z.string(),
});

/** Deterministic predicate deciding whether a branch path applies at replay time. Mirrors the
 * ai-service `BranchCheck`: `url_contains`/`text_visible` probe the live page (like `StepVerify`),
 * `param_equals` compares the run-time param named `param` against `value` — pure, no page probe. */
export const BranchCheckSchema = z.object({
  kind: z.enum(["url_contains", "text_visible", "param_equals"]),
  value: z.string(),
  /** Run-time parameter name — used by `param_equals` only; empty otherwise. */
  param: z.string().default(""),
});

/** One labelled alternate of a branch step. `check` null marks the default path (at most one,
 * evaluated last); `stepIds` binds the ordinary flat action steps (same version) that run when
 * this path is chosen — the branch step binds them, it does not nest them. */
export const SopBranchPathSchema = z.object({
  pathId: z.string(),
  label: z.string().min(1),
  check: BranchCheckSchema.nullable().default(null),
  stepIds: z.array(z.string()).default([]),
});

/** The payload of a `kind='branch'` step: a human-readable `condition` plus ≥2 labelled paths.
 * Replay evaluates each path's `check` in order (check-less default last); all-null checks fall
 * back to agentic path choice. */
export const SopBranchSchema = z.object({
  condition: z.string(),
  paths: z.array(SopBranchPathSchema).min(2),
});

/** How a composed (`sop_ref`) step binds one child parameter: to a literal, or to a parent
 * parameter passed down at run time. */
export const ParamBindingSchema = z.discriminatedUnion("from", [
  z.object({ from: z.literal("literal"), value: z.string() }),
  z.object({ from: z.literal("param"), name: z.string() }),
]);

export const SopStepKindSchema = z.enum(["action", "sop_ref", "branch"]);

/** One persisted step row. An `action` step carries `target`/`action`/`verify`; a `sop_ref`
 * step carries `refSopId`/`refVersion`/`paramBindings` (pinned composition); a `branch` step
 * carries `branch` (condition + labelled paths binding same-version action stepIds). `refName`
 * is a resolved display label, not a stored column. */
export const AgentSopStepSchema = z.object({
  id: z.string().uuid(),
  stepId: z.string(),
  ordinal: z.number().int(),
  kind: SopStepKindSchema,
  name: z.string(),
  description: z.string(),
  constraints: z.array(z.string()),
  surface: z.string().nullable(),
  provenance: z.enum(["confirmed", "provisional"]),
  // Who authored the step — the agent, or a human via takeover (AI-246 interactive). Orthogonal
  // to `provenance` (lifecycle). Recorded so a future provenance-aware heal (Phase 4) can skip
  // credential / sign-off re-prompts on human steps; not yet threaded into replay. `null` legacy.
  authorship: z.enum(["agent", "human"]).nullable().default(null),
  risk: z.enum(["low", "high"]),
  // action steps:
  target: StepTargetSchema.nullable(),
  action: StepActionSchema.nullable(),
  verify: StepVerifySchema.nullable(),
  // sop_ref (composition) steps:
  refSopId: z.string().uuid().nullable(),
  refVersion: z.number().int().nullable(),
  refName: z.string().nullable(),
  paramBindings: z.record(z.string(), ParamBindingSchema).nullable(),
  // branch steps: the stored condition + labelled paths, each binding an ordered list of
  // same-version action stepIds. Null on non-branch rows.
  branch: SopBranchSchema.nullable().default(null),
  // Derived per-step branch membership (like `refName`: resolved display fields, not stored
  // columns) — set on an action step bound into a branch path so the detail page can group it
  // under its branch/condition; null on steps outside any branch.
  branchStepId: z.string().nullable().default(null),
  branchPathId: z.string().nullable().default(null),
  branchPathLabel: z.string().nullable().default(null),
  // Lifecycle phase this step was taught under (setup / main / teardown). Drives the
  // setup/action/teardown grouping in the composition UI; `main` on rows that predate the column.
  phase: InterviewPhaseSchema.default("main"),
  /** Suppress the replay screenshot for this step (it touches a secret surface). Mirrors the
   * ai-service `RunSopStepInput.sensitive`; read-tolerant default for pre-column rows. */
  sensitive: z.boolean().default(false),
  /** An absent target at replay is a deterministic no-op success instead of a failure (e.g.
   * a popup that only sometimes appears). Mirrors `RunSopStepInput.best_effort`. */
  bestEffort: z.boolean().default(false),
});

/** Snake_case twin of `SopBranchPathSchema` as emitted by the ai-service `SopBranchPath.model_dump`
 * on the `author_sop` result_json (path_id / step_ids ride the wire verbatim, un-camelised). */
export const AuthoredSopBranchPathSchema = z.object({
  path_id: z.string(),
  label: z.string().min(1),
  check: BranchCheckSchema.nullable().default(null),
  step_ids: z.array(z.string()).default([]),
});

/** Snake_case twin of `SopBranchSchema` (ai-service `SopBranch.model_dump`). */
export const AuthoredSopBranchSchema = z.object({
  condition: z.string(),
  paths: z.array(AuthoredSopBranchPathSchema).min(2),
});

/** One step as emitted by the `author_sop` interview's `result_json` artifact (mirrors the
 * ai-service `SopStep.model_dump`, hence the snake_case `step_id`). Node maps these into
 * persisted `action` rows at finalize. */
export const AuthoredSopStepSchema = z.object({
  index: z.number().int(),
  name: z.string(),
  description: z.string().default(""),
  constraints: z.array(z.string()).default([]),
  surface: z.string().nullable().default(null),
  target: StepTargetSchema.nullable().default(null),
  action: StepActionSchema.nullable().default(null),
  verify: StepVerifySchema.nullable().default(null),
  step_id: z.string(),
  provenance: z.enum(["confirmed", "provisional"]).default("confirmed"),
  // Who demonstrated the step (AI-246): the agent, or a human via takeover. Mirrors the
  // ai-service SopStep field; default "agent" keeps older result_json payloads valid.
  authorship: z.enum(["agent", "human"]).default("agent"),
  risk: z.enum(["low", "high"]).default("high"),
  // Lifecycle phase the step was taught under (setup / main / teardown). Mirrors the
  // ai-service `SopStep.phase`; Node reads it at finalize to keep the taught steps in one composed
  // SOP, ordered setup → main → teardown. Default "main" keeps older result_json payloads valid.
  phase: InterviewPhaseSchema.default("main"),
  // Branch-aware SOPs: `kind` marks a branch step (default "action" keeps older result_json
  // payloads valid); `branch` carries its condition + labelled paths (snake_case inner keys —
  // the ai-service `SopStep.branch` rides the wire verbatim). Null on action steps.
  kind: z.enum(["action", "branch"]).default("action"),
  branch: AuthoredSopBranchSchema.nullable().default(null),
});

/** One captured gesture drained from the live page on a "capture now" turn (AI-246 free-roam).
 * Mirrors the ai-service `TakeoverHint` TypedDict verbatim (snake_case, secret-safe: password
 * fields carry `secret:true` with `value` nulled in-page, never transmitted). */
export const TrajectoryGestureSchema = z.object({
  type: z.string(),
  role: z.string().optional(),
  name: z.string().optional(),
  value: z.string().nullable().optional(),
  secret: z.boolean().optional(),
  url: z.string().optional(),
  x: z.number().optional(),
  y: z.number().optional(),
});

/** One accumulated live-capture frame (AI-246 free-roam → P2 mock_ui hand-off), as emitted on the
 * `author_sop` interview's `result_json` artifact under `trajectory[]`. Mirrors the ai-service
 * `TrajectoryFrame` TypedDict verbatim (snake_case `step_ids`; Struct keys round-trip un-camelised).
 * `screenshot` is an object-store reference present only when a screenshot uploader is configured
 * (prod); absent in local dev. `trajectory` is `[]` on the classic interview path. */
export const TrajectoryFrameSchema = z.object({
  index: z.number().int(),
  url: z.string().nullable().default(null),
  gestures: z.array(TrajectoryGestureSchema).default([]),
  step_ids: z.array(z.string()).default([]),
  screenshot: z.string().optional(),
  // P3: the demonstrated path this frame belongs to (`root` by default, `p1`/`p2`/… for alternate
  // paths). Snake_case `path_id` to match the verbatim wire shape (Struct key round-trips
  // un-camelised); a blank/absent value attributes to the root path. Frames sharing a path prefix
  // reconverge to shared states in the mock; a divergence becomes a branching edge.
  path_id: z.string().default(""),
});

/** Where a non-root path forked (P3): the shared prefix is every parent frame with
 * `index <= from_frame_index`; the frames after it diverge under `condition_label`. Mirrors the
 * ai-service `TrajectoryBranchPoint`; keys stay snake_case so the object rides the wire verbatim. */
export const TrajectoryBranchPointSchema = z.object({
  parent_path_id: z.string().default(""),
  from_frame_index: z.number().int().default(0),
  condition_label: z.string().default(""),
});

/** One alternate path in a multi-path trajectory (P3): its id + the point it forked at. The root
 * path is implicit and never listed here. Mirrors the ai-service `TrajectoryPath` /
 * `result_json.branches[]` entry verbatim (snake_case). */
export const TrajectoryBranchSchema = z.object({
  path_id: z.string().default(""),
  branch_point: TrajectoryBranchPointSchema,
});

/** The taught SOP step a capture appended/refined, projected down to what a per-screen storyboard
 * note needs (AI-340 P2). Mirrors the ai-service `TrajectorySeedStep` (snake_case `step_id`, so the
 * whole object round-trips verbatim to the Python `mock_ui.studio` config). */
export const TrajectorySeedStepSchema = z.object({
  step_id: z.string(),
  name: z.string().default(""),
  description: z.string().default(""),
  constraints: z.array(z.string()).default([]),
});

/** The P1 → P2 hand-off carried on the mock-UI start input (AI-340): a completed `author_sop`
 * walkthrough's captured `trajectory` frames + its taught `steps`. When present (and no explicit
 * `storyboard`), the `mock_ui.studio` build derives its storyboard notes + edges from it instead of
 * hand-authored notes. Mirrors the ai-service `TrajectorySeed`; keys are already snake_case so the
 * object rides the `trajectory_seed` config field verbatim (the runner only snake-cases the top-level
 * key). Invariant: `frames` stay index-aligned to the screenshot image FileParts the runner attaches
 * (one FilePart per frame); frames without a resolvable screenshot are dropped in lockstep. */
export const TrajectorySeedSchema = z.object({
  frames: z.array(TrajectoryFrameSchema).default([]),
  steps: z.array(TrajectorySeedStepSchema).default([]),
  // P3: the multi-path branch structure. Empty ⇒ a single linear path (P2 behavior unchanged);
  // non-empty ⇒ the `mock_ui.studio` build emits `storyboard_edges` so it enumerates one `?case=`
  // per demonstrated path and the state graph reconverges the shared prefix. Snake_case verbatim.
  branches: z.array(TrajectoryBranchSchema).default([]),
});

/** Value-free remote-desktop (AVD / Windows 365) login config carried by a login SOP and
 * forwarded to the run_sop executor as `session.remote_desktop_login`. Only REFERENCES cross
 * here — the VM password / OTP / auth-state are resolved by id at run time; secret values never
 * appear in the SOP row or on this schema. Mirrors the ai-service `RemoteDesktopLoginConfig`. */
export const RemoteDesktopLoginConfigSchema = z.object({
  portalUrl: z.string().url(),
  desktopName: z.string().min(1),
  /** Credential set holding the VM (remote desktop) password. */
  vmCredentialSetId: z.string().uuid().nullable().default(null),
  /**
   * Where live OTP codes are fetched from during MFA. NOT a uuid: an OTP target id is a
   * caller-chosen slug (`^[a-zA-Z0-9-]+$`, see `otp.contract.ts`) naming either a TOTP secret or an
   * SMS target — matching `remoteDesktopCredentialsConfigSchema.otpTargetId` on the RPA path and
   * `otp_target_id: str` in the Python `RemoteDesktopLoginConfig`. A `.uuid()` here rejected every
   * real-world target (e.g. `avd-mfa`) at seed/write time.
   */
  otpTargetId: z.string().min(1).nullable().default(null),
  /** The key the reusable Azure auth-state (cookies) is stored under. */
  authStateTargetId: z.string().nullable().default(null),
  /** Name the auth-state is saved under. Set per run by the remote-desktop login node (its
   * `buildAuthStateName`); absent → the ai-service falls back to `avd-<desktopName>`. */
  authStateName: z.string().min(1).nullable().optional(),
  desktopLoadWaitSeconds: z.number().min(0).max(120).optional(),
});

/** A stored SOP. `bodyMarkdown` is the authored render cache; `steps`/`parameters` are the
 * canonical executable form (current version). Mirrors `ai_agent_sops` + its current version. */
export const DEFAULT_PROCEDURE_TIMEOUT_SECONDS = 1200;
/** Two hours: long enough for a portal procedure that walks every Vendor Account (ABB, ~195). */
export const MAX_PROCEDURE_TIMEOUT_SECONDS = 7200;
export const ProcedureTimeoutSecondsSchema = z
  .number()
  .int()
  .min(10)
  .max(MAX_PROCEDURE_TIMEOUT_SECONDS);
/** Cap on agent actions one agentic replay step may take before the ai-service cancels it. Null on a
 * SOP means the platform default (50); bounds match the ai-service `ExecutionConfig.max_iterations`. */
export const ProcedureMaxIterationsSchema = z.number().int().min(1).max(200);
export const SopSessionRulesSchema = z
  .array(
    z
      .string()
      .trim()
      .min(1)
      .max(4000)
      .regex(/^[^\p{Cc}\p{Zl}\p{Zp}]+$/u),
  )
  .max(50);

export const UpdateSopSessionRulesInputSchema = z.object({
  organizationId: z.string().uuid(),
  sopId: z.string().uuid(),
  draftVersionId: z.string().uuid(),
  sessionRules: SopSessionRulesSchema,
  originalSessionRules: SopSessionRulesSchema,
});
export type UpdateSopSessionRulesInput = z.infer<
  typeof UpdateSopSessionRulesInputSchema
>;

/** One declared procedure OUTPUT — a value an agentic replay step reads off the screen and reports
 * back to the caller (e.g. the receipt number an ERP assigns on Save). The inverse of a parameter;
 * mirrors the ai-service `SopOutput`. Names use the parameter charset so step text can say
 * "report receipt_number". */
export const SopOutputSchema = z.object({
  name: z
    .string()
    .regex(/^[a-z][a-z0-9_]*$/)
    .max(64),
  description: z.string().trim().max(500).default(""),
});
export type SopOutput = z.infer<typeof SopOutputSchema>;

export const SopOutputsSchema = z
  .array(SopOutputSchema)
  .max(20)
  .refine(
    (outputs) => new Set(outputs.map((o) => o.name)).size === outputs.length,
    { message: "Output names must be unique." },
  );

export const UpdateSopOutputsInputSchema = z.object({
  organizationId: z.string().uuid(),
  sopId: z.string().uuid(),
  draftVersionId: z.string().uuid(),
  outputs: SopOutputsSchema,
  /** The outputs the editor started from — a mismatch means someone else saved first. */
  originalOutputs: z.array(SopOutputSchema),
});
export type UpdateSopOutputsInput = z.infer<typeof UpdateSopOutputsInputSchema>;

/** A procedure's completion comment: plain text with `{name}` placeholders, each filled from the
 * run's declared outputs first, then its parameters, e.g.
 * `"Keyed receipt. Receipt No. {receipt_number}, deposit No. {deposit_number}."`. A placeholder
 * with no value renders "—". Posted on the task only when a replay run for that task succeeds. */
export const SopCompletionCommentTemplateSchema = z.string().trim().max(2000);

export const AgentSopSchema = z.object({
  sessionRules: SopSessionRulesSchema.default([]),
  /** Values this version's replay reports back to the caller (declared outputs). */
  outputs: z.array(SopOutputSchema).default([]),
  id: z.string().uuid(),
  organizationId: z.string().uuid(),
  name: z.string(),
  title: z.string(),
  bodyMarkdown: z.string(),
  /** Current structured instructions; authored markdown remains separate for legacy replay. */
  readableInstructions: z.string().optional(),
  siteKey: z.string().nullable(),
  taskKey: z.string().nullable(),
  // Read-tolerant: any stored status string (not just the SopStatusSchema set).
  status: z.string(),
  version: z.number().int(),
  parameters: z.array(SopParamSchema).default([]),
  steps: z.array(AgentSopStepSchema).default([]),
  displayWidth: z.number().int().nullable(),
  displayHeight: z.number().int().nullable(),
  createdByUserId: z.string().uuid().nullable(),
  /** Preferred credential for Kernel pre-auth on replay (AI-246 follow-up). */
  loginCredentialId: z.string().uuid().nullable(),
  /** Another SOP to run when pre-auth doesn't succeed (AI-246 follow-up). */
  fallbackLoginSopId: z.string().uuid().nullable(),
  /** The domain Kernel pre-auth logs into before the browser opens, and the replay browser's
   * initial URL (AI-246 follow-up). Without this, a replay with no per-run `startUrl` has no
   * domain to pre-authenticate against, even with a `loginCredentialId` attached. */
  startUrl: z.string().url().nullable(),
  /** Set on a remote-desktop LOGIN SOP (e.g. the AVD login SOP): replaying it — standalone or
   * as another SOP's `fallbackLoginSopId` — forwards this as `session.remote_desktop_login`,
   * which arms the run_sop executor's login augmentation (by-reference VM password, live OTP,
   * reusable auth-state). Null for every ordinary SOP. Read-tolerant for older payloads. */
  remoteDesktopLogin: RemoteDesktopLoginConfigSchema.nullable().default(null),
  /** AI-340 (G1): human attestation that this SOP is non-mutating and safe to replay against the
   * real portal (read-only / shadow-first). Gates the "Run against real site" action. Default false;
   * read-tolerant so older payloads without it stay valid. */
  readOnlyAttested: z.boolean().default(false),
  /** Replay this procedure by LOOKING at the screen instead of re-aiming the DOM. Set for a
   * procedure meant to run inside a streamed remote desktop, where the page is the client chrome
   * around a video canvas and there is no accessibility tree to perceive.
   *
   * Every replay entry point forwards it, and the ai-service then also grades each step's Verify
   * post-condition from the screen — the DOM check would search the client's markup, where the
   * app's text does not exist, and fail the step it was authored to prove. Captured DOM-selector
   * code is skipped; coordinate code still runs. Default false; read-tolerant for older payloads. */
  visualOnly: z.boolean().default(false),
  procedureTimeoutSeconds:
    ProcedureTimeoutSecondsSchema.nullable().default(null),
  procedureMaxIterations: ProcedureMaxIterationsSchema.nullable().default(null),
  /** Opt-in replay hardening ("Save successful runs as replay scripts"). When true, a successful
   * healed or agentic replay step's captured code is saved as a new `self_heal` version that the
   * next run replays deterministically. Default false, because captured code carries the run's
   * literal values — a procedure that types per-run values (amounts, ids, dates) must not harden.
   * Existing scripts keep replaying either way; only new capture is gated. */
  hardenOnSuccess: z.boolean().default(false),
  /** Comment posted on the task a successful replay ran for (see
   * `SopCompletionCommentTemplateSchema`). Null → the run posts nothing. Read-tolerant. */
  completionCommentTemplate:
    SopCompletionCommentTemplateSchema.nullable().default(null),
  /** The `BuiltinSopTemplate.key` this SOP was installed from, or null for a hand-authored /
   * taught SOP. Non-null marks the row as system-managed — the UI badges it and an upgrade
   * replaces its steps with the shipped ones. */
  builtinKey: z.string().nullable().default(null),
  /** The built-in's shipped `templateVersion` at the last install. Deliberately unrelated to
   * `version`, which also advances on self-heal; comparing THIS against the catalog is what
   * answers "is this org behind?". Null on rows seeded before the column existed. */
  builtinVersion: z.number().int().nullable().default(null),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

/** List view drops the (potentially large) markdown body + step rows. */
export const AgentSopSummarySchema = AgentSopSchema.omit({
  sessionRules: true,
  outputs: true,
  bodyMarkdown: true,
  readableInstructions: true,
  steps: true,
});

// --- Built-in procedure catalog ---------------------------------------------------------------
// Procedures shipped with the product. Code-defined, so a deploy makes an entry available to
// every org with no operator action — but installing stays deliberate, because each entry needs
// org-specific setup (a desktop name, credential references) that can't exist at org-creation
// time. Installing materializes ordinary org-scoped SOP rows; there is no virtual/global SOP.

export const BuiltinSopSetupKindSchema = z.enum([
  "remote_desktop_login",
  "none",
]);

/** What the caller's org has for a catalog entry. Null when it isn't installed. */
export const BuiltinSopInstallStateSchema = z.object({
  sopId: z.string().uuid(),
  /** The SOP's own version counter. Advances on self-heal too, so it is NOT the template version. */
  sopVersion: z.number().int(),
  /** The shipped `templateVersion` recorded at the last install; null for rows seeded before the
   * column existed (matched by SOP name instead). */
  installedTemplateVersion: z.number().int().nullable(),
  updateAvailable: z.boolean(),
  /** Whether the org-specific setup is complete enough to replay. */
  configured: z.boolean(),
});

export const BuiltinSopCatalogEntrySchema = z.object({
  key: z.string(),
  title: z.string(),
  description: z.string(),
  sopName: z.string(),
  templateVersion: z.number().int(),
  setupKind: BuiltinSopSetupKindSchema,
  stepCount: z.number().int(),
  installed: BuiltinSopInstallStateSchema.nullable(),
});

export const ListBuiltinSopsInputSchema = z.object({
  organizationId: z.string().uuid(),
});

export const ListBuiltinSopsResponseSchema = z.object({
  builtins: z.array(BuiltinSopCatalogEntrySchema),
});

export const GetBuiltinSopSetupInputSchema = z.object({
  organizationId: z.string().uuid(),
  builtinKey: z.string().min(1),
});

/** Best-effort prefill for the setup dialog. REFERENCES only — never secret values. */
export const BuiltinSopSetupPrefillSchema = z.object({
  remoteDesktopLogin: RemoteDesktopLoginConfigSchema.nullable().default(null),
  loginCredentialId: z.string().uuid().nullable().default(null),
  /** Where the values came from, so the dialog can say so rather than silently pre-filling. */
  source: z.enum(["installed_sop", "agent_definition", "defaults"]),
  /** e.g. the legacy agent definition's name. Null for "defaults". */
  sourceLabel: z.string().nullable().default(null),
});

export const InstallBuiltinSopInputSchema = z.object({
  organizationId: z.string().uuid(),
  builtinKey: z.string().min(1),
  /** Required when the entry's `setupKind` is "remote_desktop_login". */
  remoteDesktopLogin: RemoteDesktopLoginConfigSchema.nullable().default(null),
  /** The PORTAL account credential set (email + password) → the SOP's `loginCredentialId`.
   * Distinct from `remoteDesktopLogin.vmCredentialSetId`, which is the in-session VM password. */
  loginCredentialId: z.string().uuid().nullable().default(null),
  fallbackForSopIds: z.array(z.string().uuid()).default([]),
  /** Rewrite the shipped steps even when the installed template version already matches. */
  force: z.boolean().default(false),
});

export const InstallBuiltinSopResponseSchema = z.object({
  sop: AgentSopSchema,
  /** False when the template version was already current — only the login config changed, and
   * the SOP's `version` did not move. */
  stepsWritten: z.boolean(),
  installedTemplateVersion: z.number().int(),
  fallbacksWired: z.number().int(),
});

export const FinalizeInterviewResponseSchema = z.object({
  /** The finalized procedure. Any setup/teardown steps taught in the session are kept in this one
   * SOP, tagged with their `phase` (see `AgentSopStepSchema.phase`) and ordered setup → main →
   * teardown; the composition UI groups them into zones and replay runs the setup steps first. */
  sop: AgentSopSchema,
});

export const ListSopsInputSchema = z.object({
  organizationId: z.string().uuid(),
  status: SopStatusSchema.optional(),
  limit: z.number().int().min(1).max(100).default(50),
  offset: z.number().int().min(0).default(0),
});

export const ListSopsResponseSchema = z.object({
  sops: z.array(AgentSopSummarySchema),
});

export const GetSopInputSchema = z.object({
  organizationId: z.string().uuid(),
  sopId: z.string().uuid(),
  /** Read a specific version (the `/v/$versionNumber` route) instead of the published one. */
  version: z.number().int().min(1).optional(),
});

export const StartSopRunInputSchema = z.object({
  organizationId: z.string().uuid(),
  userId: z.string().uuid().nullable().optional(),
  sopId: z.string().uuid(),
  startUrl: z.string().url().optional(),
  credentialSetId: z.string().uuid().nullable().optional(),
  /** Where each step's captured code runs. Defaults to the `daytona` sandbox — SOP step code is
   * arbitrary author-edited Python and must not exec in-process on the shared service. Fails
   * closed if no server Daytona key is set; `inprocess` is opt-in for trusted/local use. */
  codeRuntime: z.enum(["inprocess", "daytona"]).default("daytona"),
  /** Replay a specific version instead of the published one (AI-461) — how a draft is test-run
   * before publishing. Omitted (every schedule/agent caller) → the published version. */
  version: z.number().int().min(1).optional(),
  /** Values for the SOP's declared parameters — the root scope substituted into step code. */
  parameters: z.record(z.string(), z.string()).default({}),
  /** Credential records this replay's `credential_fill` steps may type into an app running INSIDE
   * a remote desktop (Famous) — by reference; the ai-service resolves each value itself. Each
   * becomes the flat pair `<credential name>_username` / `<credential name>_password` that a step
   * addresses. In production the `remote_desktop_execute` node supplies these; passing them here
   * is how a standalone Run exercises such a procedure while it is being developed. */
  applicationCredentialIds: z.array(z.string().uuid()).optional(),
  model: AgentRunModelInputSchema.optional(),
});

/** A run that persists a durable per-step agent trace (the operator replay AND the interview) —
 * the frontend uses `agentRunId` to fetch that trace (screenshots/tool calls/reasoning) via
 * `agentDefinitions.getRunTimeline` once the run has produced rows to show. */
export const StartRunWithTraceResponseSchema =
  StartSessionIdResponseSchema.extend({
    agentRunId: z.string().uuid(),
  });

/** AI-340 (G1): a mock-taught SOP's resolved REAL target, surfaced on `getSop` so the "Run against
 * real site" affordance can point the single `startSopRun` at it. Both null when the SOP isn't
 * mock-backed or its mock has no real portal configured. A read only — running a real replay is just
 * `startSopRun` with `startUrl = realBaseUrl`. */
/** Where a version came from (AI-461) — mirrors `AgentSopVersionOrigin` on the model. What lets
 * the history distinguish an agentic self-heal from a human edit. */
export const SopVersionOriginSchema = z.enum([
  "interview",
  "import",
  "json_import",
  "human_edit",
  "ai_edit",
  "self_heal",
  "builtin_install",
]);

export const SopVersionStatusSchema = z.enum(["draft", "finalized"]);

export const GetSopResponseSchema = AgentSopSchema.extend({
  realBaseUrl: z.string().nullable(),
  realCredentialSetId: z.string().nullable(),
  // --- versioned lifecycle (AI-461). The banner, picker, and read-only gate derive from these.
  /** The published version's row id; null only for a pre-backfill row with no finalized version. */
  pinnedVersionId: z.string().uuid().nullable().default(null),
  /** Row id of the version this response describes (`version`/`steps`/`parameters` are its). */
  versionId: z.string().uuid().nullable().default(null),
  /** Lifecycle of the version being viewed. */
  versionStatus: SopVersionStatusSchema.default("finalized"),
  versionOrigin: SopVersionOriginSchema.default("human_edit"),
  /** Author of the viewed version — what "someone else's draft is read-only" keys off. */
  versionCreatedBy: z.string().uuid().nullable().default(null),
  /** Present when this procedure was created from the review-first importer. */
  importProvenance: z
    .object({
      importId: z.string().uuid(),
      sourceKind: z.enum(["scribe", "markdown"]),
      canonicalUrl: z.string().url().nullable(),
      capturedAt: z.coerce.date(),
      contentSha256: z.string().regex(/^[a-f0-9]{64}$/),
    })
    .nullable()
    .default(null),
});

// --- SOP composition + parameter editing (AI-246) ---
// Each of these is a structural edit that supersedes the SOP with a new version; the response
// is the freshly-saved current version (steps + parameters included).

/** Embed another SOP as a step, pinned to that child's current version, binding its params. */
export const AddSopRefStepInputSchema = z.object({
  organizationId: z.string().uuid(),
  sopId: z.string().uuid(),
  /** The caller's open draft this edit writes into (AI-461). */
  draftVersionId: z.string().uuid(),
  refSopId: z.string().uuid(),
  /** 1-based insert position; appended at the end when omitted. */
  ordinal: z.number().int().min(1).optional(),
  /** Bindings keyed by the child SOP's parameter name. Required child params MUST be bound. */
  paramBindings: z.record(z.string(), ParamBindingSchema).default({}),
});

/** Add a step whose automation isn't authored yet — just a natural-language name/description
 * (+ optional RFC-2119 constraints). The run_sop replay engine's SemanticHealer improvises the
 * step live from this spec on its first run and captures durable code, which the replay writes
 * back onto the step so subsequent runs replay it deterministically. */
export const AddActionStepInputSchema = z.object({
  organizationId: z.string().uuid(),
  sopId: z.string().uuid(),
  /** The caller's open draft this edit writes into (AI-461). */
  draftVersionId: z.string().uuid(),
  name: z.string().min(1),
  description: z.string().min(1),
  constraints: z.array(z.string()).default([]),
  /**
   * Make this a deterministic `credential_fill` step instead of a spec-only one. The step is
   * persisted `sensitive` (its replay frame carries no screenshot) and is never healed, never
   * hardened into code, and never reachable by an agent — a secret must not be improvised.
   *
   * Omit for the ordinary case: a natural-language step the first successful run hardens.
   */
  credential: CredentialFillSchema.nullish(),
});

export const RemoveStepInputSchema = z.object({
  organizationId: z.string().uuid(),
  sopId: z.string().uuid(),
  /** The caller's open draft this edit writes into (AI-461). */
  draftVersionId: z.string().uuid(),
  stepId: z.string(),
});

/** Update only one draft step's text, refusing edits composed against stale text. */
export const UpdateSopStepInputSchema = z.object({
  organizationId: z.string().uuid(),
  sopId: z.string().uuid(),
  draftVersionId: z.string().uuid(),
  stepId: z.string().min(1),
  name: z.string().trim().min(1),
  description: z.string(),
  constraints: z.array(z.string()),
  original: z.object({
    name: z.string(),
    description: z.string(),
    constraints: z.array(z.string()),
  }),
});

/** Reorder a SOP's steps by their stable `stepId`. Unknown ids are ignored; any existing step
 * omitted from the list is appended in its current order, so a reorder can never drop a step. */
export const ReorderStepsInputSchema = z.object({
  organizationId: z.string().uuid(),
  sopId: z.string().uuid(),
  /** The caller's open draft this edit writes into (AI-461). */
  draftVersionId: z.string().uuid(),
  orderedStepIds: z.array(z.string()),
});

export const UpdateSopParametersInputSchema = z.object({
  organizationId: z.string().uuid(),
  sopId: z.string().uuid(),
  /** The caller's open draft this edit writes into (AI-461). */
  draftVersionId: z.string().uuid(),
  parameters: z.array(SopParamSchema),
});

/** Attach/change a procedure's own login config (AI-246 follow-up) — a preferred credential
 * for Kernel pre-auth, and/or a fallback login SOP to run when pre-auth doesn't succeed. An
 * omitted field is left as-is; pass `null` explicitly to clear it. */
export const UpdateSopLoginConfigInputSchema = z.object({
  organizationId: z.string().uuid(),
  sopId: z.string().uuid(),
  loginCredentialId: z.string().uuid().nullable().optional(),
  fallbackLoginSopId: z.string().uuid().nullable().optional(),
  startUrl: z.string().url().nullable().optional(),
  /** AI-340 (G1): flip the human read-only attestation (safe to run against the real site). */
  readOnlyAttested: z.boolean().optional(),
  /** Replay this procedure visually (see `AgentSopSchema.visualOnly`) — set for one taught inside
   * a streamed remote desktop. */
  visualOnly: z.boolean().optional(),
  procedureTimeoutSeconds: ProcedureTimeoutSecondsSchema.nullable().optional(),
  procedureMaxIterations: ProcedureMaxIterationsSchema.nullable().optional(),
  /** Opt in/out of saving successful runs as replay scripts (see `AgentSopSchema.hardenOnSuccess`). */
  hardenOnSuccess: z.boolean().optional(),
  /** Set or clear (null) the completion comment template (see
   * `AgentSopSchema.completionCommentTemplate`). A blank string clears it too. */
  completionCommentTemplate:
    SopCompletionCommentTemplateSchema.nullable().optional(),
  /** Attach/clear the SOP's remote-desktop login config (login SOPs only — see
   * `AgentSopSchema.remoteDesktopLogin`). */
  remoteDesktopLogin: RemoteDesktopLoginConfigSchema.nullable().optional(),
});

/** Rename a procedure's display name. The handle follows the title, except on a built-in,
 * whose handle is the catalog key and stays put. */
export const RenameSopInputSchema = z.object({
  organizationId: z.string().uuid(),
  sopId: z.string().uuid(),
  title: z.string().trim().min(1).max(255),
});

export const RenameSopResponseSchema = z.object({
  id: z.string().uuid(),
  title: z.string(),
  name: z.string(),
  /** Other live procedures in the org with the same title. Allowed, but agents resolve a
   * procedure by title, so the caller should warn. */
  titleSharedWith: z.array(
    z.object({ id: z.string().uuid(), name: z.string() }),
  ),
});

/** Soft-delete a procedure. Refused while a live agent, task, procedure, workflow, or run uses it. */
export const DeleteSopInputSchema = z.object({
  organizationId: z.string().uuid(),
  sopId: z.string().uuid(),
});

// --- Whole-procedure edit (AI-246 follow-up: conversational re-edit) ---

/** One step of a proposed whole-procedure edit — the editable projection of a step row.
 *
 * `target`/`action` are deliberately ABSENT: a step's captured locator and code are owned by the
 * interview capture and the replay self-heal loop, so an editor (a human editing in place, or Nami
 * proposing an edit) may reshape the natural-language spec and the structure around a step but
 * never rewrite what it actually clicks. The server carries those two fields forward verbatim from
 * the step row matching `stepId`. Omitting them from the wire shape is what enforces that — it is
 * not reachable by prompting an agent differently.
 *
 * `isNew` marks a step the edit invents. Its `stepId` is minted by the editor rather than the
 * server precisely so a `branch` path in the SAME edit can bind it — an id assigned server-side
 * would leave those bindings dangling. A new step may only be a spec-only `action` (the replay
 * engine's SemanticHealer improvises it on its first run, exactly like `addActionStep`), a
 * `sop_ref`, or a `branch`; it cannot arrive with pre-authored automation. */
export const EditableSopStepSchema = z.object({
  /** Stable logical id. Must name an existing step unless `isNew`. */
  stepId: z.string().min(1),
  /** Create this step instead of editing one. Its `stepId` must not already exist. */
  isNew: z.boolean().default(false),
  kind: SopStepKindSchema.default("action"),
  name: z.string().min(1),
  description: z.string().default(""),
  /** Standing rules the replay agent must honor while executing this step (RFC-2119 style). */
  constraints: z.array(z.string()).default([]),
  phase: InterviewPhaseSchema.default("main"),
  verify: StepVerifySchema.nullable().default(null),
  sensitive: z.boolean().default(false),
  bestEffort: z.boolean().default(false),
  // sop_ref (composition) steps — a new ref pins the child's current version when refVersion is null.
  refSopId: z.string().uuid().nullable().default(null),
  refVersion: z.number().int().nullable().default(null),
  paramBindings: z
    .record(z.string(), ParamBindingSchema)
    .nullable()
    .default(null),
  // branch steps: the condition + labelled paths binding same-edit action stepIds.
  branch: SopBranchSchema.nullable().default(null),
});

/** Apply a whole-procedure edit into the caller's DRAFT: `steps` REPLACES the draft's step list
 * in the given order (AI-461 — the published version is untouched; publishing is a separate act).
 * `baseVersionId` is optimistic concurrency: it names the version row the edit was composed
 * against, and must be the draft itself or — for the create-draft-then-apply flow — the draft's
 * parent while the draft is still a pristine copy of it. A stale base is refused rather than
 * silently clobbering other changes. */
export const ApplySopEditInputSchema = z.object({
  organizationId: z.string().uuid(),
  sopId: z.string().uuid(),
  /** The caller's open draft this edit writes into (AI-461). */
  draftVersionId: z.string().uuid(),
  /** The version row the edit was composed against (what the page seeded into Nami). */
  baseVersionId: z.string().uuid(),
  steps: z.array(EditableSopStepSchema),
  parameters: z.array(SopParamSchema).default([]),
  /** One-line description of the change, for the activity/version trail. */
  summary: z.string().default(""),
});

// --- Version history (AI-461) ---

/** One entry in a procedure's version history. `changelog` is display-ready: the backend unwraps
 * the mechanical JSON it stores before the AI pass lands, so a consumer never sees two formats. */
export const AgentSopVersionSchema = z.object({
  id: z.string().uuid(),
  version: z.number().int(),
  status: SopVersionStatusSchema,
  origin: SopVersionOriginSchema,
  parentVersionId: z.string().uuid().nullable(),
  changelog: z.string().nullable(),
  /** Whether this is the version runs/schedules/agents currently use. */
  isPinned: z.boolean(),
  createdBy: z.string().uuid().nullable(),
  createdByName: z.string().nullable(),
  createdAt: z.string(),
});

export const SopStaleBaseWarningSchema = z.object({
  kind: z.literal("stale_base"),
  draftParentVersionId: z.string().uuid().nullable(),
  draftParentVersionNumber: z.number().int().nullable(),
  currentPublishedVersionId: z.string().uuid().nullable(),
  currentPublishedVersionNumber: z.number().int().nullable(),
  message: z.string(),
});

export const ListSopVersionsInputSchema = z.object({
  organizationId: z.string().uuid(),
  sopId: z.string().uuid(),
});

export const CreateSopDraftInputSchema = z.object({
  organizationId: z.string().uuid(),
  sopId: z.string().uuid(),
  /** Fork from this version instead of the published one (the "+ Draft from v3" flow). */
  sourceVersionId: z.string().uuid().optional(),
});

export const CreateSopDraftResponseSchema = z.object({
  version: AgentSopVersionSchema,
  /** True when the caller already had an open draft, which was returned instead of forking. */
  resumed: z.boolean(),
});

/** Complete executable step accepted by the additive candidate-save seam. Database ids and
 * resolved display-only fields are server-owned; all durable behavior is supplied explicitly. */
const SopCandidateBaseStepSchema = AgentSopStepSchema.omit({
  id: true,
  refName: true,
  branchStepId: true,
  branchPathId: true,
  branchPathLabel: true,
}).extend({
  stepId: z.string().trim().min(1),
});

const SopCandidateActionSchema = z.discriminatedUnion("kind", [
  StepActionSchema.extend({
    kind: z.literal("script"),
    code: z
      .string()
      .refine((code) => code.trim().length > 0, "Script code is required."),
  }),
  StepActionSchema.extend({
    kind: z.literal("skill_ref"),
    skill: z.string().trim().min(1),
  }),
  StepActionSchema.extend({
    kind: z.literal("credential_fill"),
    credential: CredentialFillSchema,
  }),
]);

/** Write validation is stricter than the legacy-tolerant read schema. A script
 * may act on the page without an element target; verification remains optional. */
export const SopCandidateStepSchema = z.discriminatedUnion("kind", [
  SopCandidateBaseStepSchema.extend({
    kind: z.literal("action"),
    action: SopCandidateActionSchema,
    branch: z.null().default(null),
  }),
  SopCandidateBaseStepSchema.extend({
    kind: z.literal("sop_ref"),
    refSopId: z.string().uuid(),
    refVersion: z.number().int().positive(),
    paramBindings: z.record(z.string(), ParamBindingSchema),
    branch: z.null().default(null),
  }),
  SopCandidateBaseStepSchema.extend({
    kind: z.literal("branch"),
    branch: SopBranchSchema.extend({ condition: z.string().trim().min(1) }),
  }),
]);

export const SopCandidateStepsSchema = z
  .array(SopCandidateStepSchema)
  .min(1)
  .refine(
    (steps) =>
      steps.some((step) => step.kind === "action" || step.kind === "sop_ref"),
    "A candidate must contain at least one executable step.",
  );

export const CreateSopCandidateInputSchema = z.object({
  organizationId: z.string().uuid(),
  /** Stable completion/import key, scoped to the signed-in actor by the backend. */
  idempotencyKey: z.string().trim().min(1).max(255),
  /** Existing lineage to fork. Omit only for a first, unpublished procedure. */
  sopId: z.string().uuid().optional(),
  /** Explicit finalized base; omitted means the selected lineage's published pin. */
  sourceVersionId: z.string().uuid().optional(),
  name: z.string().trim().min(1).max(255),
  title: z.string().trim().min(1).max(255),
  bodyMarkdown: z.string().max(200_000),
  parameters: z.array(SopParamSchema).default([]),
  steps: SopCandidateStepsSchema,
  siteKey: z.string().nullable().optional(),
  taskKey: z.string().nullable().optional(),
  displayWidth: z.number().int().positive().nullable().optional(),
  displayHeight: z.number().int().positive().nullable().optional(),
  loginCredentialId: z.string().uuid().nullable().optional(),
  fallbackLoginSopId: z.string().uuid().nullable().optional(),
  startUrl: z.string().url().nullable().optional(),
  remoteDesktopLogin: RemoteDesktopLoginConfigSchema.nullable().optional(),
  /** The two completion sources allowed to create candidates; edit/heal provenance is not
   * caller-asserted through this endpoint. */
  origin: z.enum(["interview", "import"]).default("interview"),
});

export const CreateSopCandidateResponseSchema = z.object({
  sop: AgentSopSchema,
  candidate: z.object({
    id: z.string().uuid(),
    version: z.number().int().positive(),
    status: SopVersionStatusSchema,
  }),
  /** True only for an exact idempotent retry of the original, still-unchanged candidate. */
  resumed: z.boolean(),
});

export const FinalizeSopVersionInputSchema = z.object({
  organizationId: z.string().uuid(),
  sopId: z.string().uuid(),
  versionId: z.string().uuid(),
  /** Also publish in the same transaction ("Publish vN" — the normal path). */
  publish: z.boolean().default(true),
  /** Commit even though something else was published since this draft forked. */
  acknowledgeStaleBase: z.boolean().default(false),
  feedbackReviewAcknowledgment: z.string().optional(),
});

export const SopFeedbackReviewRequiredSchema = z.object({
  fingerprint: z.string(),
  warnings: z.array(SopImprovementImpactWarningSchema),
});

export type SopFeedbackReviewRequired = z.infer<
  typeof SopFeedbackReviewRequiredSchema
>;

export const FinalizeSopVersionResponseSchema = z.union([
  z.object({ ok: z.literal(true), version: AgentSopVersionSchema }),
  z.object({ ok: z.literal(false), warning: SopStaleBaseWarningSchema }),
  z.object({
    ok: z.literal(false),
    feedbackReviewRequired: SopFeedbackReviewRequiredSchema,
  }),
]);

export const FinalizeSopCandidateInputSchema = z.object({
  organizationId: z.string().uuid(),
  sopId: z.string().uuid(),
  versionId: z.string().uuid(),
});

export const FinalizeSopCandidateResponseSchema =
  FinalizeSopVersionResponseSchema;

export const PublishSopVersionInputSchema = z.object({
  organizationId: z.string().uuid(),
  sopId: z.string().uuid(),
  versionId: z.string().uuid(),
  acknowledgeStaleBase: z.boolean().default(false),
  feedbackReviewAcknowledgment: z.string().optional(),
});

export const PublishSopVersionResponseSchema = z.union([
  z.object({ ok: z.literal(true) }),
  z.object({ ok: z.literal(false), warning: SopStaleBaseWarningSchema }),
  z.object({
    ok: z.literal(false),
    feedbackReviewRequired: SopFeedbackReviewRequiredSchema,
  }),
]);

export const DeleteSopDraftInputSchema = z.object({
  organizationId: z.string().uuid(),
  sopId: z.string().uuid(),
  versionId: z.string().uuid(),
});

export const UpdateSopVersionChangelogInputSchema = z.object({
  organizationId: z.string().uuid(),
  sopId: z.string().uuid(),
  versionId: z.string().uuid(),
  changelog: z.string(),
});

export const RegenerateSopChangelogInputSchema = z.object({
  organizationId: z.string().uuid(),
  sopId: z.string().uuid(),
  versionId: z.string().uuid(),
});

/** Replace a literal in a step's captured code with a `params["name"]` reference and declare
 * the parameter (turns a hard-coded value into a reusable input). */
export const PromoteLiteralToParamInputSchema = z.object({
  organizationId: z.string().uuid(),
  sopId: z.string().uuid(),
  /** The caller's open draft this edit writes into (AI-461). */
  draftVersionId: z.string().uuid(),
  stepId: z.string(),
  literal: z.string().min(1),
  paramName: z.string().min(1),
  required: z.boolean().default(true),
  description: z.string().default(""),
});

// --- Mock UI Studio (AI-340) ---
// A Daytona-sandboxed coding agent that edits a static mock UI, serves a signed live preview, and
// iterates on human text+screenshot feedback until approved. Streams over the shared
// subscribeOperatorSession AG-UI stream (routed by skillId); its signed preview URL arrives on
// OperatorActivity.liveViewUrl, and on done it emits the `paths` eval-case forest (mockUiPaths).

/** A mock_ui `data:` URI image (base64 payload inline) — a HITL feedback screenshot or a
 * storyboard source frame — forwarded to the skill as an A2A image FilePart. */
export const MockUiFeedbackImageSchema = z
  .string()
  .refine((v) => v.startsWith("data:"), { message: "Expected a data: URI." });

/** Cap on the number of root→leaf paths (= `?case=` scenarios) a storyboard graph may enumerate.
 * Mirrors `MAX_STORYBOARD_PATHS` in the ai-service `storyboard_graph` module — bump both together. */
export const MAX_STORYBOARD_PATHS = 8;

/** Validate the storyboard adjacency: node indices in range, no self-loop / duplicate edge, screen
 * 0 is the single root (in-degree 0), the graph is a connected DAG, and it enumerates
 * ≤ `MAX_STORYBOARD_PATHS` root→leaf paths. Returns the first problem's message, or `null` when
 * valid. Empty edges = the implicit linear chain (always valid). Plain function so it's
 * unit-testable and reused by the RPC `superRefine`; mirrors the ai-service `enumerate_paths`. */
export function validateStoryboardGraph(
  nodeCount: number,
  edges: ReadonlyArray<{ from: number; to: number }>,
): string | null {
  if (edges.length === 0) return null;
  const succ = new Map<number, number[]>();
  const indeg = new Array<number>(nodeCount).fill(0);
  const seen = new Set<string>();
  for (const e of edges) {
    if (e.from >= nodeCount || e.to >= nodeCount) {
      return `A storyboard branch references a screen out of range (0..${nodeCount - 1}).`;
    }
    if (e.from === e.to)
      return "A storyboard branch can't point a screen at itself.";
    const key = `${e.from}->${e.to}`;
    if (seen.has(key))
      return "Duplicate storyboard branch between the same two screens.";
    seen.add(key);
    const list = succ.get(e.from) ?? [];
    list.push(e.to);
    succ.set(e.from, list);
    indeg[e.to] += 1;
  }
  if (indeg[0] !== 0) {
    return "The first screen must be the start — it can't have incoming branches.";
  }
  // DFS from the root: detect cycles, count root→leaf paths (bail past the cap), collect reachable.
  const reachable = new Set<number>();
  const onStack = new Set<number>();
  let paths = 0;
  let cyclic = false;
  const walk = (node: number): void => {
    if (cyclic || paths > MAX_STORYBOARD_PATHS) return;
    reachable.add(node);
    const outs = succ.get(node);
    if (outs === undefined || outs.length === 0) {
      paths += 1;
      return;
    }
    onStack.add(node);
    for (const next of outs) {
      if (onStack.has(next)) {
        cyclic = true;
        return;
      }
      walk(next);
    }
    onStack.delete(node);
  };
  walk(0);
  if (cyclic)
    return "The storyboard branches form a loop — screens must flow forward.";
  if (paths > MAX_STORYBOARD_PATHS) {
    return `Too many paths through the storyboard (max ${MAX_STORYBOARD_PATHS}). Simplify the branches.`;
  }
  if (reachable.size !== nodeCount) {
    return "Every screen must connect to the start — remove or link the stray screens.";
  }
  return null;
}

export const StartMockUiInputSchema = z
  .object({
    organizationId: z.string().uuid(),
    userId: z.string().uuid().nullable().optional(),
    /** What the human wants built/changed in the mock UI — seeds the coding agent. */
    instruction: z.string().min(1),
    /** Which mock under the seed root to edit. Defaults to `famous-mock-ui` server-side. */
    site: z.string().min(1).max(255).optional(),
    model: AgentRunModelInputSchema.optional(),
    /** Source screenshots + inline notes — the "storyboard" nodes. Seeds the build (each frame is a
     * screen the agent reproduces 1:1) and is persisted for side-by-side comparison. Images are
     * self-contained `data:` URIs so they ride the exported artifact and never expire. A node's id
     * on the wire is its index here. */
    storyboard: z
      .array(
        z.object({
          image: MockUiFeedbackImageSchema,
          note: z.string().max(2000).default(""),
        }),
      )
      .max(10)
      .default([]),
    /** Branches between storyboard screens: `from`/`to` are node indices into `storyboard`, `label`
     * is the user action. Empty ⇒ the implicit linear chain 0→1→…→N-1 (byte-identical to a flat
     * storyboard). Each distinct root→leaf path becomes one `?case=` the agent builds. Validated as
     * a connected, acyclic, path-capped DAG rooted at screen 0 (see `validateStoryboardGraph`). */
    storyboardEdges: z
      .array(
        z.object({
          from: z.number().int().nonnegative(),
          to: z.number().int().nonnegative(),
          label: z.string().max(200).default(""),
        }),
      )
      .max(20)
      .default([]),
    /** AI-340 (P2): seed the build from a completed `author_sop` walkthrough instead of a
     * hand-authored `storyboard` — the studio derives the storyboard notes + edges from the
     * captured trajectory (Python `unified_studio.adapter`). Maps to the snake_case
     * `trajectory_seed` config field; a non-empty `storyboard` always wins over a seed. Normally set
     * by the `generateStudioBundle` orchestration, not the frontend directly. */
    trajectorySeed: TrajectorySeedSchema.optional(),
  })
  .superRefine((val, ctx) => {
    const problem = validateStoryboardGraph(
      val.storyboard.length,
      val.storyboardEdges,
    );
    if (problem) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: problem,
        path: ["storyboardEdges"],
      });
    }
  });

// --- Unified live-capture Studio bundle (AI-340 P2) ---
// The result of driving the Build → Teach → Verify loop from one completed SOP-capture: the
// auto-generated mock (published + its file/diff deliverables), the eval golden captured by
// replaying the taught SOP against that mock, and the taught SOP id that ties them together.
// Mirrors the ai-service `StudioBundle` (`by_alias` dump); `expected_eval_state` keeps snake_case
// because it is the golden the procedure-replay scorer reads by that name.

/** The generated mock as it lands in the bundle. `versionRef` is the immutable publish key
 * (`<site>/v<version>` for a cataloged build, else null); `files`/`diff` are the mock_ui
 * `file:<name>` / `diff` deliverables. */
export const StudioMockArtifactSchema = z.object({
  /** Durable catalog key for the generated mock. */
  site: z.string(),
  hostedUrl: z.string(),
  versionRef: z.string().nullable().default(null),
  files: z.record(z.string(), z.string()).default({}),
  diff: z.string().default(""),
});

/** The captured golden for the replayed eval case: the `?case=` id and the `window.__evalState` a
 * clean `run_sop` replay observed against the generated mock. */
export const StudioEvalGoldenSchema = z.object({
  case: z.string(),
  // snake_case: this is the golden the deterministic procedure-replay scorer reads by name.
  expected_eval_state: z.record(z.string(), z.unknown()).default({}),
});

export const StudioBundleSchema = z.object({
  mock: StudioMockArtifactSchema,
  /** One golden per replayed eval case, in demonstrated-path order (root path first). A linear
   * walkthrough carries exactly one entry; a branched walkthrough one per demonstrated path. */
  evalGoldens: z.array(StudioEvalGoldenSchema).default([]),
  sopId: z.string(),
  /** Deterministic procedure-replay suite created for this generated mock. */
  evalSuiteId: z.string().uuid(),
});

/** Run the unified pipeline from a completed `author_sop` capture (AI-340 P2): seed a `mock_ui.studio`
 * build from the captured trajectory, then replay the taught SOP against the generated mock to
 * capture the golden `__evalState`. `sopSessionId` is the completed free-roam interview session the
 * trajectory was captured in; `sopId` is that session's finalized SOP (the taught procedure replayed
 * for the golden). */
export const GenerateStudioBundleInputSchema = z.object({
  organizationId: z.string().uuid(),
  sopSessionId: z.string().uuid(),
  sopId: z.string().uuid(),
  /** Which mock under the seed root to build into. Defaults server-side (`famous-mock-ui`). */
  site: z.string().min(1).max(255).optional(),
  /** Extra instruction for the coding agent (the trajectory already seeds the storyboard). */
  instruction: z.string().min(1).optional(),
  /** Concrete values for the finalized SOP's declared run-time parameters. */
  parameterValues: z.record(z.string(), z.string()).default({}),
  model: AgentRunModelInputSchema.optional(),
});

export const GetStudioBundleInputSchema = z.object({
  organizationId: z.string().uuid(),
  /** The mock-build session id returned by `generateStudioBundle`. */
  sessionId: z.string().uuid(),
});

/** Which per-case golden replay the pipeline is on ("Replaying path 2 of 3…"): 1-based `current`
 * of `total` demonstrated paths, plus the diverging condition label of the path being replayed
 * (`""` for the root path). Non-null only while `status: "replaying"`. */
export const StudioReplayProgressSchema = z.object({
  current: z.number().int(),
  total: z.number().int(),
  conditionLabel: z.string().default(""),
});

/** Poll the pipeline started by `generateStudioBundle`: `bundle` is null until the mock has published
 * AND every per-case golden replay has finished (`status: "ready"`); `error` carries the failure
 * reason when `status: "failed"`. */
export const GetStudioBundleResponseSchema = z.object({
  bundle: StudioBundleSchema.nullable().default(null),
  status: z
    .enum(["building", "replaying", "creating_eval", "ready", "failed"])
    .default("building"),
  error: z.string().nullable().default(null),
  replayProgress: StudioReplayProgressSchema.nullable().default(null),
});

export const RespondToMockUiInputSchema = z.object({
  organizationId: z.string().uuid(),
  sessionId: z.string().uuid(),
  /** The human's reply to the agent's request_feedback pause (approval or a correction). */
  feedback: z.string().min(1),
  /** Optional screenshots to accompany the reply (data: URIs). */
  images: z.array(MockUiFeedbackImageSchema).max(6).default([]),
});

/** The mock UIs available to seed for a "modify existing" build. Listed via an org-authed
 * procedure so the set can be gated per organization. */
export const ListMockUiSitesInputSchema = z.object({
  organizationId: z.string().uuid(),
});

export const MockUiSiteSchema = z.object({
  /** Directory name under the mock seed root — passed as `site` to startMockUiStudio. */
  name: z.string(),
  /** Human label shown in the selector. */
  title: z.string(),
  /** Optional subtitle shown under the title. */
  description: z.string().nullable().optional(),
  /** The org's latest published hostedUrl for this mock (AI-340), or null if never published —
   * lets the studio teach/eval a mock without a live build session. */
  hostedUrl: z.string().nullable().optional(),
  /** The eval suite accumulating this mock's runs (AI-385), for the catalog's "connected suite ↗"
   * jump. Null until the mock has been evaluated at least once. */
  evalSuiteId: z.string().nullable().optional(),
});

export const ListMockUiSitesResponseSchema = z.object({
  sites: z.array(MockUiSiteSchema),
});

export const StartMockUiExploreInputSchema = z.object({
  organizationId: z.string().uuid(),
  site: z.string().min(1).max(255),
});

export const StartMockUiExploreResponseSchema = z.object({
  sessionToken: z.string().min(1),
  liveViewUrl: z.string().url(),
});

export const CloseMockUiExploreInputSchema = z.object({
  organizationId: z.string().uuid(),
  sessionToken: z.string().min(1),
});

/** Fetch a mock's persisted state machine, SOP-projected (AI-340): the latest published build's
 * state-graph skeleton with `path.sopId` + terminal `node.golden` overlaid from its taught SOPs.
 * For the no-live-session view (revisiting a published mock). Null when the org never published it. */
export const GetMockUiStateGraphInputSchema = z.object({
  organizationId: z.string().uuid(),
  site: z.string().min(1).max(255),
});

export const GetMockUiStateGraphResponseSchema = z.object({
  graph: MockUiStateGraphSchema.nullable(),
});

/** The version history of a mock (AI-340) for the studio's version-history panel: each published
 * build is an immutable version carrying its AI-generated changelog + a link to the AgentRun that
 * built it. Newest first. */
export const GetMockUiVersionsInputSchema = z.object({
  organizationId: z.string().uuid(),
  site: z.string().min(1).max(255),
});

export const MockUiVersionSchema = z.object({
  version: z.number().int(),
  hostedUrl: z.string(),
  /** AI-generated "what changed since v{N-1}" summary; null for builds authored before changelogs. */
  changelog: z.string().nullable(),
  /** The AgentRun that built this version — the trace link ("view build trace"). */
  agentRunId: z.string().nullable(),
  createdAt: z.date(),
});

/** A mock's opt-in automation toggles + real-site target (AI-340). Toggles default false; the
 * real-target fields default null. */
export const MockUiConfigSchema = z.object({
  /** Auto-enqueue the deterministic offline eval of taught procedures on each new version. */
  autoReEvalOnChange: z.boolean(),
  /** Surface a reality-stale warning when the current version's online replays are degraded. */
  flagRealityStale: z.boolean(),
  /** AI-340 (G1): the real customer portal this mock stands in for — the `startUrl` a read-only
   * real-site replay of its taught SOPs uses. Null until configured. */
  realBaseUrl: z.string().nullable(),
  /** AI-340 (G1): the credential set Kernel pre-auth uses on the real portal. Null = none. */
  realCredentialSetId: z.string().nullable(),
});

/** The current version's reality-stale status (AI-340 Step 10). All-off unless `flagRealityStale`
 * is on; `realityStale` true ⇒ online production replays are degraded (real site likely drifted). */
export const MockUiRealityStatusSchema = z.object({
  enabled: z.boolean(),
  realityStale: z.boolean(),
  onlineRunCount: z.number().int(),
  onlineSuccessRate: z.number().nullable(),
  onlineDriftRate: z.number().nullable(),
});

export const GetMockUiVersionsResponseSchema = z.object({
  /** The current (highest) version pointer, or null when the mock has never been published. */
  currentVersion: z.number().int().nullable(),
  versions: z.array(MockUiVersionSchema),
  /** The mock's opt-in automation toggles. */
  config: MockUiConfigSchema,
  /** Whether the current version looks reality-stale (only computed when the toggle is on). */
  reality: MockUiRealityStatusSchema,
  /** The eval suite accumulating this mock's runs, for a "view eval runs" deep-link. Null until the
   * mock has been evaluated at least once (AI-340). */
  evalSuiteId: z.string().nullable(),
});

/** Set a mock's opt-in automation toggles (AI-340). Omitted fields are left unchanged. */
export const SetMockUiConfigInputSchema = z.object({
  organizationId: z.string().uuid(),
  site: z.string().min(1).max(255),
  autoReEvalOnChange: z.boolean().optional(),
  flagRealityStale: z.boolean().optional(),
  /** AI-340 (G1): set/clear the real portal URL. Empty string clears it (→ null). */
  realBaseUrl: z.string().nullable().optional(),
  /** AI-340 (G1): set/clear the real portal's credential set. */
  realCredentialSetId: z.string().uuid().nullable().optional(),
});

export const SetMockUiConfigResponseSchema = z.object({
  /** False when the mock isn't cataloged for this org yet (build/publish it first). */
  ok: z.boolean(),
});

/**
 * Human-authored contract overlay for a mock (the Studio node-inspector edits). Persisted on the
 * mock's catalog row and merged onto the derived state graph on read, with the human value winning
 * over the coding agent's draft — the same "materialize-on-read overlay" pattern AI-340 uses for
 * `path.sopId` / terminal `node.golden`, so edits survive an agent rebuild (keyed by state label).
 */
export const MockUiContractOverlaySchema = z.object({
  /** Per-state precondition / prohibitions, keyed by the node's state label. */
  states: z
    .record(
      z.string(),
      z.object({
        precondition: z.string().default(""),
        prohibitions: z.array(z.string()).default([]),
      }),
    )
    .default({}),
  /** Mock-level run-time parameters (declared once). Overrides the agent-derived list when non-empty. */
  parameters: z.array(MockUiParamSchema).default([]),
});
export type MockUiContractOverlay = z.infer<typeof MockUiContractOverlaySchema>;

/** Save a mock's human-authored contract overlay (AI-340 follow-up; Studio node inspector). */
export const SetMockUiContractInputSchema = z.object({
  organizationId: z.string().uuid(),
  site: z.string().min(1).max(255),
  overlay: MockUiContractOverlaySchema,
});

export const SetMockUiContractResponseSchema = z.object({
  /** False when the mock isn't cataloged for this org yet (build/publish it first). */
  ok: z.boolean(),
});

/** Golden-capture status for a mock-backed SOP (AI-340 U3) — surfaced on the SOP detail page so
 * capturing the golden isn't a silent dead-end. */
export const GetSopGoldenStatusInputSchema = z.object({
  organizationId: z.string().uuid(),
  sopId: z.string().uuid(),
});

export const SopGoldenStatusSchema = z.object({
  hasGolden: z.boolean(),
  /** The `?case=` ids holding a captured golden (`""` = the implicit linear case; a legacy
   * single-golden row surfaces as `[""]`), sorted. Defaulted so the field stays additive. */
  goldenCaseIds: z.array(z.string()).default([]),
  /** The mock version the golden was captured against; null when no golden. */
  goldenMockVersion: z.number().int().nullable(),
  /** The mock's current version; null for a non-mock-backed SOP. */
  currentMockVersion: z.number().int().nullable(),
  /** goldenMockVersion < currentMockVersion ⇒ recapture. */
  isStale: z.boolean(),
  /** The latest replay's heal + outcome, to explain a missing golden. Null if never replayed. */
  lastRun: z
    .object({ hadHeals: z.boolean(), succeeded: z.boolean() })
    .nullable(),
});

/**
 * Agent Management Contracts
 */
export const agentContracts = {
  // Get session status
  getSessionStatus: oc
    .input(GetSessionStatusInputSchema)
    .output(SessionStatusResponseSchema)
    .route({
      method: "GET",
      summary: "Get browser agent session status",
      description:
        "Returns a browser agent session's status, timing, error, screenshots, live messages, live view link, recording and any saved login state.",
    }),

  // Cancel a running session
  cancelSession: oc
    .input(CancelSessionInputSchema)
    .output(CancelSessionResponseSchema)
    .route({
      method: "POST",
      summary: "Cancel a browser agent session",
      description: "Stops a running browser agent session in the org.",
    }),

  // List saved auth states
  listAuthStates: oc
    .input(ListAuthStatesInputSchema)
    .output(ListAuthStatesResponseSchema)
    .route({
      method: "GET",
      summary: "List saved browser logins",
      description:
        "Pages through the org's saved browser login states agents reuse to skip signing in: site, username, last use, expiry and whether expired.",
    }),

  // Delete an auth state
  deleteAuthState: oc
    .input(DeleteAuthStateInputSchema)
    .output(DeleteAuthStateResponseSchema)
    .route({
      method: "DELETE",
      summary: "Delete a saved browser login",
      description:
        "Deletes one saved browser login state so agents must sign in to that site again.",
    }),
  // Resolve session ID for an agent run (from in-memory session manager)
  getSessionForAgentRun: oc
    .input(
      z.object({
        organizationId: z.string().uuid(),
        agentRunId: z.string().uuid(),
      }),
    )
    .output(
      z.object({
        sessionId: z.string().uuid().nullable(),
      }),
    )
    .route({
      method: "GET",
      summary: "Find the browser session for an agent run",
      description:
        "Returns the live browser session id driving an agent run, or null when none is active.",
    }),
  // Live operator session event stream (AG-UI). Live phase only — completed runs
  // read getRunTimeline. Consumed by the live operator UI;
  // the poll (getSessionStatus) stays as fallback.
  subscribeOperatorSession: oc
    .input(SubscribeOperatorSessionInputSchema)
    .output(eventIterator(OperatorAgUiEventSchema))
    .route({
      method: "POST",
      summary: "Stream a live browser agent session",
      description:
        "Streams live events from a browser agent session (messages, steps, screenshots, pauses) for teaching, replay and mock-site build sessions.",
    }),

  // --- Bidirectional interview → SOP authoring (AI-166) ---
  // Start an `operator.author_sop` interview; subscribe via subscribeOperatorSession.
  startInterview: oc
    .input(StartInterviewInputSchema)
    .output(StartRunWithTraceResponseSchema)
    .route({
      method: "POST",
      summary: "Start teaching a procedure",
      description:
        "Starts a live session where a person teaches the browser agent a procedure (SOP) on a website, with optional start URL, credentials, setup procedure and check-in policy.",
    }),
  // Answer the interview's pending confirm (resumes the input-required pause).
  respondToInterview: oc
    .input(RespondToInterviewInputSchema)
    .output(RespondToInterviewResponseSchema)
    .route({
      method: "POST",
      summary: "Answer the teaching agent",
      description:
        "Replies to the teaching agent's pending confirmation with yes, a correction or a retraction, and resumes the session.",
    }),
  // "Jump in": interrupt a running turn at its next tool boundary so the human can guide it.
  interruptInterview: oc
    .input(InterruptInterviewInputSchema)
    .output(RespondToInterviewResponseSchema)
    .route({
      method: "POST",
      summary: "Interrupt the teaching agent",
      description:
        "Pauses a running procedure-teaching session at the agent's next tool step so the person can guide it; does not cancel.",
    }),
  // Resume the pending confirm with an on-screen annotation (drawn marks) instead of typed text.
  submitInterviewAnnotation: oc
    .input(SubmitInterviewAnnotationInputSchema)
    .output(SubmitInterviewAnnotationResponseSchema)
    .route({
      method: "POST",
      summary: "Answer the teaching agent with screen marks",
      description:
        "Resumes a paused procedure-teaching session with marks drawn on the live screen and an optional note, instead of typed text.",
    }),
  // Resume the pending confirm after a human takeover; the agent reconciles the captured gestures.
  submitInterviewTakeover: oc
    .input(SubmitInterviewTakeoverInputSchema)
    .output(SubmitInterviewTakeoverResponseSchema)
    .route({
      method: "POST",
      summary: "Hand control back after a takeover",
      description:
        "Resumes a procedure-teaching session after the person drove the browser themselves; the agent turns their recorded actions into procedure steps.",
    }),
  // "Capture now" in a free-roam session: drain the gestures + screenshot into an incremental SOP step.
  submitInterviewCapture: oc
    .input(SubmitInterviewCaptureInputSchema)
    .output(SubmitInterviewCaptureResponseSchema)
    .route({
      method: "POST",
      summary: "Capture a step while teaching",
      description:
        "In a free-roam teaching session, records what the person just did in the browser, with a screenshot, as the next procedure step.",
    }),
  // Declare which lifecycle phase (setup / main / teardown) the session is now teaching; steps
  // captured afterward attribute to it, and finalize persists each step's phase on the one SOP.
  setInterviewPhase: oc
    .input(SetInterviewPhaseInputSchema)
    .output(SetInterviewPhaseResponseSchema)
    .route({
      method: "POST",
      summary: "Set the phase being taught",
      description:
        "Marks whether the teaching session is now capturing setup, main or teardown steps, so later steps are tagged with that phase.",
    }),
  // "Start an alternate path" in a free-roam session (P3): fork the trajectory under a stated
  // condition so the captures that follow attribute to a new branch (a conditional mock edge).
  submitInterviewBranch: oc
    .input(SubmitInterviewBranchInputSchema)
    .output(SubmitInterviewBranchResponseSchema)
    .route({
      method: "POST",
      summary: "Start an alternate path while teaching",
      description:
        "Forks the procedure being taught under a named condition so the steps captured next form an alternate branch.",
    }),
  // Resume a request_credentials pause with a newly-provided (already-persisted) credential.
  submitInterviewCredentials: oc
    .input(SubmitInterviewCredentialsInputSchema)
    .output(SubmitInterviewCredentialsResponseSchema)
    .route({
      method: "POST",
      summary: "Give the teaching agent credentials",
      description:
        "Resumes a teaching session paused at a login wall, pointing it at a saved credential.",
    }),
  // Replace the draft's run-time parameters during capture (human add/edit/remove).
  updateInterviewParameters: oc
    .input(UpdateInterviewParametersInputSchema)
    .output(UpdateInterviewParametersResponseSchema)
    .route({
      method: "POST",
      summary: "Set a taught procedure's parameters",
      description:
        "Replaces the run-time parameters of the procedure being taught (add, edit, remove) during capture.",
    }),
  // Persist the finalized .sop.md as an ai_agent_sops row.
  finalizeInterview: oc
    .input(FinalizeInterviewInputSchema)
    .output(FinalizeInterviewResponseSchema)
    .route({
      method: "POST",
      summary: "Save a taught procedure",
      description:
        "Saves the procedure captured in a teaching session as a stored procedure (SOP) with a unique name, title and optional site and task keys.",
    }),
  // Import an existing markdown SOP as a new procedure (steps parsed deterministically).
  importSop: oc.input(ImportSopInputSchema).output(AgentSopSchema).route({
    method: "POST",
    summary: "Import a procedure from markdown",
    description:
      "Creates a new procedure (SOP) from markdown text, parsing its steps; optionally lets AI restructure an unrecognized document into steps.",
  }),
  startSopImport: oc
    .input(StartSopImportInputSchema)
    .output(StartSopImportResponseSchema)
    .route({
      method: "POST",
      summary: "Start importing a procedure",
      description:
        "Starts a background import of a procedure (SOP) from a step-by-step guide link or markdown, producing a candidate with screenshots to review.",
    }),
  getSopImport: oc
    .input(GetSopImportInputSchema)
    .output(SopImportSchema)
    .route({
      method: "GET",
      summary: "Get a procedure import",
      description:
        "Returns one procedure import's status, source, candidate steps, screenshots and any error.",
    }),
  retrySopImport: oc
    .input(RetrySopImportInputSchema)
    .output(StartSopImportResponseSchema)
    .route({
      method: "POST",
      summary: "Retry a procedure import",
      description:
        "Re-runs a failed procedure import in the background, downloading its source again.",
    }),
  discardSopImport: oc
    .input(DiscardSopImportInputSchema)
    .output(SopImportSchema)
    .route({
      method: "POST",
      summary: "Discard a procedure import",
      description:
        "Discards a ready or failed procedure import and its downloaded screenshots without creating a procedure.",
    }),
  acceptSopImport: oc
    .input(AcceptSopImportInputSchema)
    .output(AgentSopSchema)
    .route({
      method: "POST",
      summary: "Accept a procedure import",
      description:
        "Creates a procedure (SOP) from a reviewed import candidate, with optional name and reviewed screenshot guide frames.",
    }),
  // The version's visual guide (frames + bindings + mode) — AI-488 follow-on.
  listSopGuide: oc
    .input(ListSopGuideInputSchema)
    .output(ListSopGuideResponseSchema)
    .route({
      method: "GET",
      summary: "Get a procedure's visual guide",
      description:
        "Returns the annotated screenshots (frames) of a procedure version's visual guide, their step bindings and when the agent uses the guide; defaults to the published version.",
    }),
  setSopGuideMode: oc
    .input(SetSopGuideModeInputSchema)
    .output(SetSopGuideModeResponseSchema)
    .route({
      method: "POST",
      summary: "Set when a procedure's guide is used",
      description:
        "Sets on the caller's draft whether replay uses the visual guide never, only when re-performing a failed step, or always.",
    }),
  saveSopGuideAnnotations: oc
    .input(SaveSopGuideAnnotationsInputSchema)
    .output(SopGuideFrameSchema)
    .route({
      method: "POST",
      summary: "Save annotations on a guide screenshot",
      description:
        "Replaces the drawn annotations on one visual-guide screenshot in the caller's procedure draft.",
    }),
  bindSopGuideFrame: oc
    .input(BindSopGuideFrameInputSchema)
    .output(SopGuideFrameSchema)
    .route({
      method: "POST",
      summary: "Link a guide screenshot to steps",
      description:
        "Sets which procedure steps one visual-guide screenshot illustrates, in the caller's draft.",
    }),
  reorderSopGuideFrames: oc
    .input(ReorderSopGuideFramesInputSchema)
    .output(SopGuideFramesResponseSchema)
    .route({
      method: "POST",
      summary: "Reorder a procedure's guide screenshots",
      description:
        "Sets the order of the visual-guide screenshots in the caller's procedure draft.",
    }),
  removeSopGuideFrame: oc
    .input(RemoveSopGuideFrameInputSchema)
    .output(z.object({ ok: z.literal(true) }))
    .route({
      method: "POST",
      summary: "Remove a guide screenshot",
      description:
        "Removes one screenshot from the visual guide in the caller's procedure draft.",
    }),
  addSopGuideUpload: oc
    .input(AddSopGuideUploadInputSchema)
    .output(SopGuideFrameSchema)
    .route({
      method: "POST",
      summary: "Upload a guide screenshot",
      description:
        "Adds an uploaded image (up to 8 MB) to the caller's procedure draft guide, with optional label, screen type and linked steps.",
    }),
  refreshSopGuideFromRun: oc
    .input(RefreshSopGuideFromRunInputSchema)
    .output(SopGuideFramesResponseSchema)
    .route({
      method: "POST",
      summary: "Refresh guide screenshots from a run",
      description:
        "Replaces the draft guide's images with screenshots from a clean replay (latest by default), keeping step links and annotations.",
    }),
  sopGuideDrift: oc
    .input(SopGuideDriftInputSchema)
    .output(SopGuideDriftResponseSchema)
    .route({
      method: "GET",
      summary: "Check a procedure guide for outdated screenshots",
      description:
        "Compares a version's guide screenshots with a replay's screenshots and flags frames whose screen has visibly changed.",
    }),
  // List stored SOPs (summary; no markdown body).
  listSops: oc.input(ListSopsInputSchema).output(ListSopsResponseSchema).route({
    method: "GET",
    summary: "List procedures",
    description:
      "Pages through the org's stored agent procedures (SOPs), optionally by status, without their full text.",
  }),
  // Read one stored SOP (full markdown body) + its resolved real-site target (AI-340 G1).
  getSop: oc.input(GetSopInputSchema).output(GetSopResponseSchema).route({
    method: "GET",
    summary: "Get a procedure",
    description:
      "Returns one stored procedure (SOP) with full text, steps, parameters, login settings and real-site target; the published version unless one is named.",
  }),
  // Start an `operator.run_sop` deterministic replay; subscribe via subscribeOperatorSession. A
  // "real site" run is just this with `startUrl` set to the mock's real portal — one run path.
  startSopRun: oc
    .input(StartSopRunInputSchema)
    .output(StartRunWithTraceResponseSchema)
    .route({
      method: "POST",
      summary: "Run a procedure",
      description:
        "Starts a browser agent replay of a procedure (SOP), published or a chosen version, with parameter values, optional start URL and credentials.",
    }),
  // --- SOP composition + parameter editing (AI-246) ---
  // Embed another SOP (pinned to its current version). Returns the new current version.
  addSopRefStep: oc
    .input(AddSopRefStepInputSchema)
    .output(AgentSopSchema)
    .route({
      method: "POST",
      summary: "Embed a procedure as a step",
      description:
        "Inserts another procedure as a step in the caller's draft, pinned to its current version, binding its parameters.",
    }),
  // Add a step with just a natural-language spec, no automation yet. Returns the new version.
  addActionStep: oc
    .input(AddActionStepInputSchema)
    .output(AgentSopSchema)
    .route({
      method: "POST",
      summary: "Add a described step to a procedure",
      description:
        "Adds a step described in plain language (or a credential fill) to the caller's draft; the first successful run turns it into repeatable automation.",
    }),
  // Remove a step by its stable stepId. Returns the new current version.
  removeStep: oc.input(RemoveStepInputSchema).output(AgentSopSchema).route({
    method: "POST",
    summary: "Remove a step from a procedure",
    description: "Deletes one step from the caller's procedure draft.",
  }),
  // Update one step's text in the caller's draft.
  updateSopStep: oc
    .input(UpdateSopStepInputSchema)
    .output(AgentSopSchema)
    .route({
      method: "POST",
      summary: "Edit a procedure step",
      description:
        "Changes one step's name, description and constraints in the caller's draft; refuses if the step changed since the edit began.",
    }),
  // Reorder a SOP's steps by stepId. Returns the draft's new state.
  reorderSteps: oc.input(ReorderStepsInputSchema).output(AgentSopSchema).route({
    method: "POST",
    summary: "Reorder a procedure's steps",
    description:
      "Sets the order of steps in the caller's procedure draft; steps not listed keep their relative order at the end.",
  }),
  // Replace the SOP's declared parameters. Returns the new current version.
  updateSopSessionRules: oc
    .input(UpdateSopSessionRulesInputSchema)
    .output(AgentSopSchema)
    .route({
      method: "POST",
      summary: "Edit a procedure's session rules",
      description:
        "Replaces the session rules in the caller's procedure draft; refuses if they changed since the edit began.",
    }),
  updateSopParameters: oc
    .input(UpdateSopParametersInputSchema)
    .output(AgentSopSchema)
    .route({
      method: "POST",
      summary: "Edit a procedure's parameters",
      description:
        "Replaces the declared run-time parameters in the caller's procedure draft.",
    }),
  // Replace the draft's declared outputs (values a replay reports back). Returns the draft.
  updateSopOutputs: oc
    .input(UpdateSopOutputsInputSchema)
    .output(AgentSopSchema)
    .route({
      method: "POST",
      summary: "Edit a procedure's outputs",
      description:
        "Replaces the declared outputs in the caller's procedure draft: the values a replay reports back. Returns the updated draft.",
    }),
  // Apply a whole-procedure edit into the caller's draft. Returns the draft's new state.
  applySopEdit: oc.input(ApplySopEditInputSchema).output(AgentSopSchema).route({
    method: "POST",
    summary: "Apply an edit to a procedure draft",
    description:
      "Replaces the steps and parameters of the caller's procedure draft with an edited set, with a change summary; the published version is untouched.",
  }),

  // --- Version history (AI-461) ---
  // Save/resume a complete unpublished candidate. This never moves the live pin.
  createSopCandidate: oc
    .input(CreateSopCandidateInputSchema)
    .output(CreateSopCandidateResponseSchema)
    .route({
      method: "POST",
      summary: "Save a procedure candidate version",
      description:
        "Saves a complete unpublished procedure version from a teaching session or import, new or forked from an existing one. Never changes the live version.",
    }),
  // Freeze a candidate for review/eval while keeping publish as a separate operation.
  finalizeSopCandidate: oc
    .input(FinalizeSopCandidateInputSchema)
    .output(FinalizeSopCandidateResponseSchema)
    .route({
      method: "POST",
      summary: "Freeze a procedure candidate for review",
      description:
        "Locks an unpublished procedure candidate version for review or evaluation without publishing it.",
    }),
  // A SOP's versions, newest first.
  listSopVersions: oc
    .input(ListSopVersionsInputSchema)
    .output(z.object({ versions: z.array(AgentSopVersionSchema) }))
    .route({
      method: "GET",
      summary: "List a procedure's versions",
      description:
        "Lists a procedure's versions newest first, with draft or finalized status, changelog, and which one runs currently use.",
    }),
  // Fork a personal draft from the published (or a named) version, or resume the caller's own.
  createSopDraft: oc
    .input(CreateSopDraftInputSchema)
    .output(CreateSopDraftResponseSchema)
    .route({
      method: "POST",
      summary: "Start a draft of a procedure",
      description:
        "Opens the caller's personal draft of a procedure, copied from the published or a chosen version, or resumes their existing draft.",
    }),
  // Freeze a draft (optionally publishing it) — refuses with a stale-base warning by default.
  finalizeSopVersion: oc
    .input(FinalizeSopVersionInputSchema)
    .output(FinalizeSopVersionResponseSchema)
    .route({
      method: "POST",
      summary: "Finalize a procedure draft",
      description:
        "Locks a procedure draft as a version and, by default, publishes it for runs; refuses if another version was published since it began unless acknowledged.",
    }),
  // Point runs at a finalized version (publish / roll back / roll forward).
  publishSopVersion: oc
    .input(PublishSopVersionInputSchema)
    .output(PublishSopVersionResponseSchema)
    .route({
      method: "POST",
      summary: "Publish a procedure version",
      description:
        "Points runs, schedules and agents at a finalized procedure version, to publish, roll back or roll forward.",
    }),
  // Delete the caller's own unpublished draft.
  deleteSopDraft: oc
    .input(DeleteSopDraftInputSchema)
    .output(z.object({ ok: z.literal(true) }))
    .route({
      method: "POST",
      summary: "Delete a procedure draft",
      description: "Deletes the caller's own unpublished procedure draft.",
    }),
  // Overwrite a version's changelog (the one mutable field on a finalized row).
  updateSopVersionChangelog: oc
    .input(UpdateSopVersionChangelogInputSchema)
    .output(z.object({ ok: z.literal(true) }))
    .route({
      method: "POST",
      summary: "Edit a procedure version's changelog",
      description: "Overwrites the changelog text of one procedure version.",
    }),
  // Re-derive a version's mechanical changelog from its stored steps.
  regenerateSopChangelog: oc
    .input(RegenerateSopChangelogInputSchema)
    .output(z.object({ changelog: z.string().nullable() }))
    .route({
      method: "POST",
      summary: "Regenerate a procedure version's changelog",
      description:
        "Rebuilds one procedure version's changelog from its stored steps and returns it.",
    }),
  // Promote a hard-coded literal in a step to a reusable parameter. Returns the new version.
  promoteLiteralToParam: oc
    .input(PromoteLiteralToParamInputSchema)
    .output(AgentSopSchema)
    .route({
      method: "POST",
      summary: "Turn a step value into a parameter",
      description:
        "Replaces a hard-coded value in a step's automation with a new named parameter in the caller's procedure draft.",
    }),
  // Attach/change the SOP's own login config. Returns the (version-unchanged) current SOP.
  updateSopLoginConfig: oc
    .input(UpdateSopLoginConfigInputSchema)
    .output(AgentSopSchema)
    .route({
      method: "POST",
      summary: "Update a procedure's login and run settings",
      description:
        "Sets a procedure's login credential, fallback login procedure, start URL, real-site safety attestation, visual-only replay, timeout, script saving or remote-desktop login.",
    }),
  renameSop: oc
    .input(RenameSopInputSchema)
    .output(RenameSopResponseSchema)
    .route({
      method: "POST",
      summary: "Rename a procedure",
      description:
        "Changes a procedure's display title; its handle follows the title except for built-in procedures.",
    }),
  deleteSop: oc
    .input(DeleteSopInputSchema)
    .output(z.object({ ok: z.literal(true) }))
    .route({
      method: "POST",
      summary: "Delete a procedure",
      description:
        "Deletes a procedure; refused while a live agent, task, procedure, workflow or run uses it.",
    }),

  // --- Built-in procedure catalog ---
  // The product's shipped procedures + this org's install state for each.
  listBuiltinSops: oc
    .input(ListBuiltinSopsInputSchema)
    .output(ListBuiltinSopsResponseSchema)
    .route({
      method: "GET",
      summary: "List built-in procedures",
      description:
        "Lists the procedures shipped with the product and, for each, whether this org has installed it and whether its setup is complete.",
    }),
  // Prefill for the setup dialog, derived from an existing install or a legacy remote-desktop
  // agent definition. References only — never secrets.
  getBuiltinSopSetup: oc
    .input(GetBuiltinSopSetupInputSchema)
    .output(BuiltinSopSetupPrefillSchema)
    .route({
      method: "GET",
      summary: "Get setup defaults for a built-in procedure",
      description:
        "Returns prefilled setup values for installing a built-in procedure, taken from an existing install or legacy agent; references only, never secrets.",
    }),
  // Install, reconfigure, or upgrade a built-in for the caller's org.
  installBuiltinSop: oc
    .input(InstallBuiltinSopInputSchema)
    .output(InstallBuiltinSopResponseSchema)
    .route({
      method: "POST",
      summary: "Install a built-in procedure",
      description:
        "Installs, reconfigures or upgrades a built-in procedure for the org with its login credential, remote-desktop login and the procedures it backs up.",
    }),

  // --- Mock UI Studio (AI-340) ---
  // Start a `mock_ui.studio` build; subscribe via subscribeOperatorSession, resume via respondToMockUi.
  startMockUiStudio: oc
    .input(StartMockUiInputSchema)
    .output(StartRunWithTraceResponseSchema)
    .route({
      method: "POST",
      summary: "Start building a mock site",
      description:
        "Starts an AI coding session that builds or changes a mock copy of a web portal from an instruction and storyboard screenshots, for safely rehearsing procedures.",
    }),
  // Reply to the agent's request_feedback pause with text + optional screenshots (resumes the run).
  respondToMockUi: oc
    .input(RespondToMockUiInputSchema)
    .output(RespondToInterviewResponseSchema)
    .route({
      method: "POST",
      summary: "Reply to the mock site builder",
      description:
        "Answers the mock-site building agent's feedback request with text and optional screenshots, resuming the build.",
    }),
  // Resume a confirm_action pause after the user drove the interactive preview (AI-340): the skill
  // drains the gestures they performed + the resulting window.__evalState back to the agent.
  respondToMockUiTakeover: oc
    .input(SubmitInterviewTakeoverInputSchema)
    .output(SubmitInterviewTakeoverResponseSchema)
    .route({
      method: "POST",
      summary: "Hand control back to the mock site builder",
      description:
        "Resumes the mock-site building agent after the person used the interactive preview, passing along what they did.",
    }),
  // List the mock UIs available to seed for a "modify existing" build (org-authed).
  listMockUiSites: oc
    .input(ListMockUiSitesInputSchema)
    .output(ListMockUiSitesResponseSchema)
    .route({
      method: "GET",
      summary: "List mock sites",
      description:
        "Lists the mock web portals this org can build on or modify.",
    }),
  // Launch/close a plain Kernel browser pointed at a published mock's private S3 URL.
  startMockUiExplore: oc
    .input(StartMockUiExploreInputSchema)
    .output(StartMockUiExploreResponseSchema)
    .route({
      method: "POST",
      summary: "Open a mock site in a browser",
      description:
        "Launches a browser on a published mock site and returns a live view link and session token.",
    }),
  closeMockUiExplore: oc
    .input(CloseMockUiExploreInputSchema)
    .output(z.object({ ok: z.boolean() }))
    .route({
      method: "POST",
      summary: "Close a mock site browser",
      description: "Closes a browser session opened to explore a mock site.",
    }),
  // The SOP-projected state machine of a mock's latest published build (no live session needed).
  getMockUiStateGraph: oc
    .input(GetMockUiStateGraphInputSchema)
    .output(GetMockUiStateGraphResponseSchema)
    .route({
      method: "GET",
      summary: "Get a mock site's screen map",
      description:
        "Returns the screens and transitions of a mock site's latest published build, with the procedures that cover each path; null if never published.",
    }),
  // The version history of a mock (changelog + build-trace link per version), newest first, plus its
  // opt-in automation config + current-version reality-stale status.
  getMockUiVersions: oc
    .input(GetMockUiVersionsInputSchema)
    .output(GetMockUiVersionsResponseSchema)
    .route({
      method: "GET",
      summary: "List a mock site's versions",
      description:
        "Lists a mock site's published versions newest first, with AI changelog and build run, plus its automation settings and whether it is out of date.",
    }),
  // Set a mock's opt-in automation toggles (auto re-eval on change / reality-stale flag).
  setMockUiConfig: oc
    .input(SetMockUiConfigInputSchema)
    .output(SetMockUiConfigResponseSchema)
    .route({
      method: "POST",
      summary: "Update a mock site's settings",
      description:
        "Sets a mock site's automation toggles (re-evaluate on change, flag when outdated) and its real portal URL and credentials.",
    }),
  // Save a mock's human-authored contract overlay (per-state precondition/prohibitions +
  // mock-level parameters), merged onto the derived state graph on read (human wins).
  setMockUiContract: oc
    .input(SetMockUiContractInputSchema)
    .output(SetMockUiContractResponseSchema)
    .route({
      method: "POST",
      summary: "Save a mock site's screen rules",
      description:
        "Saves human-written rules for a mock site: per-screen preconditions and prohibited actions, and site parameters.",
    }),
  // Golden-capture status for a mock-backed SOP (has/stale + last-run heals), for the SOP page.
  getSopGoldenStatus: oc
    .input(GetSopGoldenStatusInputSchema)
    .output(SopGoldenStatusSchema)
    .route({
      method: "GET",
      summary: "Get a procedure's reference result status",
      description:
        "Reports whether a mock-backed procedure has captured reference end states, whether they are outdated against the mock, and how the last replay went.",
    }),
  // --- Unified live-capture Studio (AI-340 P2) ---
  // Seed a mock_ui.studio build from a completed author_sop capture, then replay the taught SOP
  // against the generated mock for the golden __evalState. Returns the mock-build session handle;
  // subscribe via subscribeOperatorSession to stream/drive the build, then poll getStudioBundle.
  generateStudioBundle: oc
    .input(GenerateStudioBundleInputSchema)
    .output(StartRunWithTraceResponseSchema)
    .route({
      method: "POST",
      summary: "Build a mock site from a taught procedure",
      description:
        "Builds a mock site from a completed teaching session, then replays the taught procedure against it to capture reference results; returns the build session.",
    }),
  // Read back the assembled StudioBundle for a generateStudioBundle session (null until ready).
  getStudioBundle: oc
    .input(GetStudioBundleInputSchema)
    .output(GetStudioBundleResponseSchema)
    .route({
      method: "GET",
      summary: "Get a mock site build bundle",
      description:
        "Returns the result of building a mock site from a taught procedure once it and its reference replays finish, or the failure reason.",
    }),
} as const;

// Export types
export type AgentSessionStatus = z.infer<typeof AgentSessionStatusSchema>;
export type AgentAuthState = z.infer<typeof AgentAuthStateSchema>;
export type GetSessionStatusInput = z.infer<typeof GetSessionStatusInputSchema>;
export type SessionStatusResponse = z.infer<typeof SessionStatusResponseSchema>;
export type CancelSessionInput = z.infer<typeof CancelSessionInputSchema>;
export type CancelSessionResponse = z.infer<typeof CancelSessionResponseSchema>;
export type ListAuthStatesInput = z.infer<typeof ListAuthStatesInputSchema>;
export type ListAuthStatesResponse = z.infer<
  typeof ListAuthStatesResponseSchema
>;
export type DeleteAuthStateInput = z.infer<typeof DeleteAuthStateInputSchema>;
export type DeleteAuthStateResponse = z.infer<
  typeof DeleteAuthStateResponseSchema
>;
export type SessionMessageEntry = z.infer<typeof SessionMessageEntrySchema>;
export type ScreenshotEntry = z.infer<typeof ScreenshotEntrySchema>;
export type OperatorActivity = z.infer<typeof OperatorActivitySchema>;
export type SopStepView = z.infer<typeof SopStepViewSchema>;
export type SopParamView = z.infer<typeof SopParamViewSchema>;
export type SopReplayStepView = z.infer<typeof SopReplayStepViewSchema>;
export type SopDraftView = z.infer<typeof SopDraftViewSchema>;
export type UpdateInterviewParametersInput = z.infer<
  typeof UpdateInterviewParametersInputSchema
>;
export type SopReplayView = z.infer<typeof SopReplayViewSchema>;
export type SopReplayRunningStep = z.infer<typeof SopReplayRunningStepSchema>;
export type OperatorStatePatchOp = z.infer<typeof OperatorStatePatchOpSchema>;
export type OperatorAgUiEvent = z.infer<typeof OperatorAgUiEventSchema>;
export type SubscribeOperatorSessionInput = z.infer<
  typeof SubscribeOperatorSessionInputSchema
>;
// Bidirectional interview → SOP authoring (AI-166)
export type StartInterviewInput = z.infer<typeof StartInterviewInputSchema>;
export type StartSessionIdResponse = z.infer<
  typeof StartSessionIdResponseSchema
>;
export type RespondToInterviewInput = z.infer<
  typeof RespondToInterviewInputSchema
>;
export type InterruptInterviewInput = z.infer<
  typeof InterruptInterviewInputSchema
>;
export type AnnotationIntent = z.infer<typeof AnnotationIntentSchema>;
export type AnnotationStroke = z.infer<typeof AnnotationStrokeSchema>;
export type SubmitInterviewAnnotationInput = z.infer<
  typeof SubmitInterviewAnnotationInputSchema
>;
export type SubmitInterviewTakeoverInput = z.infer<
  typeof SubmitInterviewTakeoverInputSchema
>;
export type SubmitInterviewCaptureInput = z.infer<
  typeof SubmitInterviewCaptureInputSchema
>;
export type InterviewPhase = z.infer<typeof InterviewPhaseSchema>;
export type SetInterviewPhaseInput = z.infer<
  typeof SetInterviewPhaseInputSchema
>;
export type SetInterviewPhaseResponse = z.infer<
  typeof SetInterviewPhaseResponseSchema
>;
export type SubmitInterviewBranchInput = z.infer<
  typeof SubmitInterviewBranchInputSchema
>;
export type TrajectoryGesture = z.infer<typeof TrajectoryGestureSchema>;
export type TrajectoryFrame = z.infer<typeof TrajectoryFrameSchema>;
export type TrajectoryBranchPoint = z.infer<typeof TrajectoryBranchPointSchema>;
export type TrajectoryBranch = z.infer<typeof TrajectoryBranchSchema>;
export type TrajectorySeedStep = z.infer<typeof TrajectorySeedStepSchema>;
export type TrajectorySeed = z.infer<typeof TrajectorySeedSchema>;
export type SubmitInterviewCredentialsInput = z.infer<
  typeof SubmitInterviewCredentialsInputSchema
>;
export type FinalizeInterviewInput = z.infer<
  typeof FinalizeInterviewInputSchema
>;
export type FinalizeInterviewResponse = z.infer<
  typeof FinalizeInterviewResponseSchema
>;
export type ImportSopInput = z.infer<typeof ImportSopInputSchema>;
export type SopImportSource = z.infer<typeof SopImportSourceSchema>;
export type SopImportStatus = z.infer<typeof SopImportStatusSchema>;
export type SopImportCandidate = z.infer<typeof SopImportCandidateSchema>;
export type SopImport = z.infer<typeof SopImportSchema>;
export type StartSopImportInput = z.infer<typeof StartSopImportInputSchema>;
export type AcceptSopImportInput = z.infer<typeof AcceptSopImportInputSchema>;
export type AcceptSopImportFrameInput = z.infer<
  typeof AcceptSopImportFrameInputSchema
>;
export type SopGuideMode = z.infer<typeof SopGuideModeSchema>;
export type SopGuideScreenType = z.infer<typeof SopGuideScreenTypeSchema>;
export type GuideAnnotation = z.infer<typeof GuideAnnotationSchema>;
export type GuideAnnotationIntent = z.infer<typeof GuideAnnotationIntentSchema>;
export type GuideFrameSource = z.infer<typeof GuideFrameSourceSchema>;
export type SopGuideFrame = z.infer<typeof SopGuideFrameSchema>;
export type ListSopGuideInput = z.infer<typeof ListSopGuideInputSchema>;
export type ListSopGuideResponse = z.infer<typeof ListSopGuideResponseSchema>;
export type SetSopGuideModeInput = z.infer<typeof SetSopGuideModeInputSchema>;
export type SaveSopGuideAnnotationsInput = z.infer<
  typeof SaveSopGuideAnnotationsInputSchema
>;
export type BindSopGuideFrameInput = z.infer<
  typeof BindSopGuideFrameInputSchema
>;
export type ReorderSopGuideFramesInput = z.infer<
  typeof ReorderSopGuideFramesInputSchema
>;
export type RemoveSopGuideFrameInput = z.infer<
  typeof RemoveSopGuideFrameInputSchema
>;
export type AddSopGuideUploadInput = z.infer<
  typeof AddSopGuideUploadInputSchema
>;
export type RefreshSopGuideFromRunInput = z.infer<
  typeof RefreshSopGuideFromRunInputSchema
>;
export type SopGuideDriftInput = z.infer<typeof SopGuideDriftInputSchema>;
export type SopGuideDriftResponse = z.infer<typeof SopGuideDriftResponseSchema>;
export type SopStatusValue = z.infer<typeof SopStatusSchema>;
export type StepTarget = z.infer<typeof StepTargetSchema>;
export type CredentialFill = z.infer<typeof CredentialFillSchema>;
export type StepAction = z.infer<typeof StepActionSchema>;
export type StepVerify = z.infer<typeof StepVerifySchema>;
export type RemoteDesktopLoginConfig = z.infer<
  typeof RemoteDesktopLoginConfigSchema
>;
export type SopParam = z.infer<typeof SopParamSchema>;
export type ParamBinding = z.infer<typeof ParamBindingSchema>;
export type SopStepKind = z.infer<typeof SopStepKindSchema>;
export type BranchCheck = z.infer<typeof BranchCheckSchema>;
export type SopBranchPath = z.infer<typeof SopBranchPathSchema>;
export type SopBranch = z.infer<typeof SopBranchSchema>;
export type AgentSopStep = z.infer<typeof AgentSopStepSchema>;
export type AuthoredSopStep = z.infer<typeof AuthoredSopStepSchema>;
export type AgentSopDto = z.infer<typeof AgentSopSchema>;
export type AgentSopSummary = z.infer<typeof AgentSopSummarySchema>;
export type ListSopsInput = z.infer<typeof ListSopsInputSchema>;
export type ListSopsResponse = z.infer<typeof ListSopsResponseSchema>;
export type GetSopInput = z.infer<typeof GetSopInputSchema>;
export type StartSopRunInput = z.infer<typeof StartSopRunInputSchema>;
export type GetSopResponse = z.infer<typeof GetSopResponseSchema>;
export type StartRunWithTraceResponse = z.infer<
  typeof StartRunWithTraceResponseSchema
>;
export type AddSopRefStepInput = z.infer<typeof AddSopRefStepInputSchema>;
export type AddActionStepInput = z.infer<typeof AddActionStepInputSchema>;
export type RemoveStepInput = z.infer<typeof RemoveStepInputSchema>;
export type ReorderStepsInput = z.infer<typeof ReorderStepsInputSchema>;
export type UpdateSopParametersInput = z.infer<
  typeof UpdateSopParametersInputSchema
>;
export type PromoteLiteralToParamInput = z.infer<
  typeof PromoteLiteralToParamInputSchema
>;
export type EditableSopStep = z.infer<typeof EditableSopStepSchema>;
export type ApplySopEditInput = z.infer<typeof ApplySopEditInputSchema>;
// Version history (AI-461)
export type SopVersionOrigin = z.infer<typeof SopVersionOriginSchema>;
export type SopVersionStatus = z.infer<typeof SopVersionStatusSchema>;
export type AgentSopVersionEntry = z.infer<typeof AgentSopVersionSchema>;
export type SopStaleBaseWarning = z.infer<typeof SopStaleBaseWarningSchema>;
export type CreateSopDraftInput = z.infer<typeof CreateSopDraftInputSchema>;
export type CreateSopDraftResponse = z.infer<
  typeof CreateSopDraftResponseSchema
>;
export type SopCandidateStep = z.infer<typeof SopCandidateStepSchema>;
export type CreateSopCandidateInput = z.infer<
  typeof CreateSopCandidateInputSchema
>;
export type CreateSopCandidateResponse = z.infer<
  typeof CreateSopCandidateResponseSchema
>;
export type FinalizeSopCandidateInput = z.infer<
  typeof FinalizeSopCandidateInputSchema
>;
export type FinalizeSopCandidateResponse = z.infer<
  typeof FinalizeSopCandidateResponseSchema
>;
export type FinalizeSopVersionInput = z.infer<
  typeof FinalizeSopVersionInputSchema
>;
export type FinalizeSopVersionResponse = z.infer<
  typeof FinalizeSopVersionResponseSchema
>;
export type PublishSopVersionInput = z.infer<
  typeof PublishSopVersionInputSchema
>;
export type PublishSopVersionResponse = z.infer<
  typeof PublishSopVersionResponseSchema
>;
export type DeleteSopDraftInput = z.infer<typeof DeleteSopDraftInputSchema>;
export type UpdateSopVersionChangelogInput = z.infer<
  typeof UpdateSopVersionChangelogInputSchema
>;
export type RegenerateSopChangelogInput = z.infer<
  typeof RegenerateSopChangelogInputSchema
>;
export type UpdateSopLoginConfigInput = z.infer<
  typeof UpdateSopLoginConfigInputSchema
>;
export type RenameSopInput = z.infer<typeof RenameSopInputSchema>;
export type RenameSopResponse = z.infer<typeof RenameSopResponseSchema>;
export type DeleteSopInput = z.infer<typeof DeleteSopInputSchema>;
// Built-in procedure catalog
export type BuiltinSopSetupKind = z.infer<typeof BuiltinSopSetupKindSchema>;
export type BuiltinSopInstallState = z.infer<
  typeof BuiltinSopInstallStateSchema
>;
export type BuiltinSopCatalogEntry = z.infer<
  typeof BuiltinSopCatalogEntrySchema
>;
export type BuiltinSopSetupPrefill = z.infer<
  typeof BuiltinSopSetupPrefillSchema
>;
export type InstallBuiltinSopInput = z.infer<
  typeof InstallBuiltinSopInputSchema
>;
export type InstallBuiltinSopResponse = z.infer<
  typeof InstallBuiltinSopResponseSchema
>;
// Mock UI Studio (AI-340)
export type MockUiEvalCase = z.infer<typeof MockUiEvalCaseSchema>;
export type MockUiPaths = z.infer<typeof MockUiPathsSchema>;
export type MockUiStoryboard = z.infer<typeof MockUiStoryboardSchema>;
export type MockUiStateNode = z.infer<typeof MockUiStateNodeSchema>;
export type MockUiStateEdge = z.infer<typeof MockUiStateEdgeSchema>;
export type MockUiStatePath = z.infer<typeof MockUiStatePathSchema>;
export type MockUiStateGraph = z.infer<typeof MockUiStateGraphSchema>;
export type StartMockUiInput = z.infer<typeof StartMockUiInputSchema>;
export type RespondToMockUiInput = z.infer<typeof RespondToMockUiInputSchema>;
// Unified live-capture Studio bundle (AI-340 P2)
export type StudioMockArtifact = z.infer<typeof StudioMockArtifactSchema>;
export type StudioEvalGolden = z.infer<typeof StudioEvalGoldenSchema>;
export type StudioReplayProgress = z.infer<typeof StudioReplayProgressSchema>;
export type StudioBundle = z.infer<typeof StudioBundleSchema>;
export type GenerateStudioBundleInput = z.infer<
  typeof GenerateStudioBundleInputSchema
>;
export type GetStudioBundleInput = z.infer<typeof GetStudioBundleInputSchema>;
export type GetStudioBundleResponse = z.infer<
  typeof GetStudioBundleResponseSchema
>;
export type MockUiSite = z.infer<typeof MockUiSiteSchema>;
export type ListMockUiSitesInput = z.infer<typeof ListMockUiSitesInputSchema>;
export type ListMockUiSitesResponse = z.infer<
  typeof ListMockUiSitesResponseSchema
>;
export type StartMockUiExploreInput = z.infer<
  typeof StartMockUiExploreInputSchema
>;
export type CloseMockUiExploreInput = z.infer<
  typeof CloseMockUiExploreInputSchema
>;
export type MockUiVersion = z.infer<typeof MockUiVersionSchema>;
export type GetMockUiVersionsResponse = z.infer<
  typeof GetMockUiVersionsResponseSchema
>;
export type MockUiConfig = z.infer<typeof MockUiConfigSchema>;
export type MockUiRealityStatus = z.infer<typeof MockUiRealityStatusSchema>;
export type SetMockUiConfigInput = z.infer<typeof SetMockUiConfigInputSchema>;
export type SetMockUiContractInput = z.infer<
  typeof SetMockUiContractInputSchema
>;
export type SopGoldenStatus = z.infer<typeof SopGoldenStatusSchema>;

export type UpdateSopStepInput = z.infer<typeof UpdateSopStepInputSchema>;
